/*
 * MouseHandlers - Maus-Event-Behandlung
 *
 * Verarbeitet alle Maus-Interaktionen auf dem Canvas:
 * - Click/Mousedown/Mouseup für Pane-Wechsel, Cursor-Positionierung, Drag-Selection
 * - Mousemove für Hover-Effekte, Console-Focus, Drag-Weitergabe
 * - Wheel für Scrollen in Editor- und Console-Panes
 * - Contextmenu für Rechtsklick-Menü
 */

import { ctx } from './appContext.js'
import { PaneId, PaneState, ConsoleFocus, TIMING, EDITOR, TOUCH } from './config.js'
import { saveSettings } from './persistence.js'
import { buildContextMenuContext, dispatchContextAction } from './contextMenuActions.js'
import { switchToProject } from './main.js'
import { effectiveEditorBounds, calcOutlinePaneWidth, calcContextPaneWidth, handleZoom, activatePane } from './keyboardHandlers.js'
import { InertiaController, computeVelocity } from './InertiaController.js'
import { scrollProfiler } from './ScrollProfiler.js'
import { isSingleWordSelection } from './spellcheck/Tokenizer.js'
import { spellCheckFieldFromPaneId as _spellCheckFieldFromPaneId } from './spellcheck/fieldRouting.js'
import { Position } from './editor/EditorModel.js'
import { focusKeyboardSink, blurKeyboardSink } from './touchKeyboard.js'
import {
    twoFingerDistance, isWithinBounds, isWithinBoundsWithSlop,
    findNearestHitWithSlop, hitTestSelectionEndpoint
} from './touchGeometry.js'

/**
 * Berechnet lokale Koordinaten relativ zu einem Pane.
 * Zentralisiert die wiederkehrende Transformation aus globalen Char/Pixel-Positionen.
 *
 * @param {Object} pane - Pane-Objekt mit innerBounds
 * @param {number} charX - Globale Char-X-Position
 * @param {number} charY - Globale Char-Y-Position (ganzzahlig)
 * @param {number} fractionalCharY - Globale fraktionale Char-Y-Position
 * @param {number} pixelX - Globale Pixel-X-Position
 * @param {number} [pixelY] - Globale Pixel-Y-Position (optional, nur für Core)
 * @returns {{ x: number, y: number, intY: number, pixelX: number, pixelY: number }}
 */
function _localCoords(pane, charX, charY, fractionalCharY, pixelX, pixelY) {
    const ib = pane.innerBounds
    return {
        x: charX - ib.x,
        y: fractionalCharY - ib.y,
        intY: charY - ib.y,
        pixelX: pixelX - ib.x * ctx.renderer.charWidth,
        pixelY: pixelY != null ? pixelY - ib.y * ctx.renderer.charHeight : undefined
    }
}

/**
 * Bestimmt den Pane, in dem die Scrollbar angezeigt wird.
 * Priorität: Main > Outline > Context (erster sichtbarer gewinnt).
 *
 * @param {Object} layoutObj
 * @returns {Object|null} Pane-Objekt oder null
 */
export function getScrollbarPane(layoutObj) {
    for (const id of [PaneId.MAIN, PaneId.OUTLINE, PaneId.CONTEXT]) {
        const pane = layoutObj.panes[id]
        if (pane && pane.isExpanded() && pane.isVisible()) return pane
    }
    return null
}

/**
 * Liefert den ProseEditor + Pane, der gerade fuer Scrollbar-Render,
 * Mausrad-Scrolling und Scrollbar-Klick/Drag zustaendig ist.
 *
 * Wenn Memory- oder PersonalDict-Pane im FULLSCREEN-Modus laufen, sind
 * MAIN/OUTLINE/CONTEXT unsichtbar (Bounds 0,0,0,0). Der Haupt-Editor
 * (`ctx.editor`) ist dann zwar instantiiert, hat aber gar keinen sichtbaren
 * Pane — Scrollbar und Mausrad muessen auf die jeweilige Pane-Editor-
 * Instanz mit ihrer eigenen ProseEditor-View umgebogen werden.
 *
 * @param {Object} ctxObj
 * @returns {{ editor: Object, pane: Object }|null}
 */
export function getActiveScrollSource(ctxObj) {
    if (ctxObj.memoryEditor && ctxObj.memoryEditor.isActive()
        && ctxObj.memoryEditor.proseEditor) {
        const pane = ctxObj.layout.panes[PaneId.MEMORY]
        if (pane && pane.isVisible()) {
            return { editor: ctxObj.memoryEditor.proseEditor, pane }
        }
    }
    if (ctxObj.personalDictEditor && ctxObj.personalDictEditor.isActive()
        && ctxObj.personalDictEditor.proseEditor) {
        const pane = ctxObj.layout.panes[PaneId.PERSONAL_DICT]
        if (pane && pane.isVisible()) {
            return { editor: ctxObj.personalDictEditor.proseEditor, pane }
        }
    }
    const sbPane = getScrollbarPane(ctxObj.layout)
    if (sbPane && ctxObj.editor) {
        return { editor: ctxObj.editor, pane: sbPane }
    }
    return null
}

/**
 * Aktualisiert alle Pane-Größen nach Tab-Bar Ein-/Ausblenden.
 * Bottom-Anker: Text bleibt visuell stabil, indem die untere Kante
 * des Viewports fixiert wird (Breite ändert sich nicht → gleiche Zeilen).
 * Im Quarter-Spacing-Modus pixel-genau über die Quarter-Position.
 */
function _resizeAfterTabBarChange() {
    if (!ctx.editor) return
    const mainPane = ctx.layout.panes[PaneId.MAIN]
    const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
    const contextPane = ctx.layout.panes[PaneId.CONTEXT]

    // Mindestens ein oberes Pane muss sichtbar sein
    const anyExpanded = (mainPane?.isExpanded()) || (outlinePane?.isExpanded()) || (contextPane?.isExpanded())
    if (!anyExpanded) return

    const view = ctx.editor.view
    const useQ = view.renderer && view.renderer.isProportional()
        && view._lineQuarterY.length > 0

    // Bottom-Position merken (vor Resize)
    // WICHTIG: view.height statt innerBounds.height — Bounds sind schon aktualisiert,
    // aber view.height ist noch der alte Wert (vor resize())
    let bottomQ = 0
    if (useQ) {
        // Quarter-genau: Viewport-Top-Quarter + alte Pane-Höhe in Quarters
        const quarterH = view.renderer.charHeight / 4
        const vpTopQ = (view._lineQuarterY[view.viewportStart] || 0)
            + view._viewportPixelOffset / quarterH
        bottomQ = vpTopQ + view.height * 4
    } else {
        // Monospace: zeilenbasiert
        bottomQ = view.viewportStart + view.height
    }

    // Resize mit exakt denselben Parametern wie syncEditorSize() —
    // verhindert, dass syncEditorSize() im nächsten Frame einen zweiten
    // Resize mit Middle-Anker-Restoration triggert, der den Bottom-Anker überschreibt
    ctx.editor.resize(effectiveEditorBounds(), calcOutlinePaneWidth(), calcContextPaneWidth())

    // Bottom-Position wiederherstellen — Text bewegt sich nicht
    if (useQ) {
        // Neuer Viewport-Top in Quarters = Bottom minus neue Pane-Höhe
        const quarterH = view.renderer.charHeight / 4
        const newTopQ = bottomQ - view.height * 4

        // Zeile und Pixel-Offset aus Quarter-Position berechnen
        let lineIdx = 0
        for (let i = view._lineQuarterY.length - 1; i >= 0; i--) {
            if (view._lineQuarterY[i] <= newTopQ) {
                lineIdx = i
                break
            }
        }
        view.viewportStart = Math.max(0, lineIdx)
        view._viewportPixelOffset = (newTopQ - (view._lineQuarterY[lineIdx] || 0)) * quarterH
        view._clampViewport()
    } else {
        // Monospace: zeilenbasiert
        view.viewportStart = Math.max(0, bottomQ - view.height)
    }
    view._scrollDelta = 0
    view._scrollPixelDelta = 0
    if (ctx.coreConsole) {
        const corePane = ctx.layout.panes[PaneId.CORE]
        if (corePane && corePane.isExpanded()) {
            ctx.coreConsole.updateBounds(corePane.innerBounds)
        }
    }
    ctx.screen.markDirty()
}

/**
 * Behandelt Mouse-Down Events (Drag-Initialisierung).
 *
 * @param {MouseEvent} e
 */
export function handleMouseDown(e) {
    // Nur linke Maustaste
    if (e.button !== 0) return

    if (!ctx.editor) return

    const { charX, charY, pixelX, pixelY, fractionalCharY } = ctx.renderer.getCharPosition(e)

    // Project Dashboard offen (Stufe E Drag&Drop): mousedown auf einen
    // Folder/Project-Eintrag registriert einen Drag-Candidate. Der
    // nachfolgende click-Event entscheidet, ob es ein normaler Klick wird
    // (kein Drag → Selektion / Open) oder ob handleMouseUp ein Drop
    // committet. **Kein** `_overlayConsumedMouseDown`-Flag setzen — sonst
    // verschluckt die handleClick-Pipeline auch die Selektions-Klicks
    // (ohne Drag); Drag-Konflikt blockiert der Dashboard-eigene
    // `_justFinishedDrag`-Schutz.
    //
    // Dialog hat Vorrang — wenn ein Confirm/Info ueber dem Dashboard
    // liegt, soll mousedown den Dialog respektieren (analog zur
    // handleClick-Pipeline weiter unten).
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen
            && !(ctx.dialogManager && ctx.dialogManager.isDialogOpen())) {
        ctx.projectDashboard.handleMouseDown(charX, charY)
        return  // Dashboard ist modal: kein Editor-Pfad
    }

    // Context Menu hat Priorität wenn offen
    if (ctx.contextMenu && ctx.contextMenu.isOpen) {
        const result = ctx.contextMenu.handleClick(charX, charY)
        // Klick wird vom Menü konsumiert und das Menü geschlossen, BEVOR die
        // Action dispatched wird. Sonst würde eine Action, die eine Exception
        // wirft, das Flag und das Schließen überspringen — das Menü bliebe
        // offen und der nachfolgende click-Event leckte in den Editor durch
        // (positioniert Cursor + active-Highlight). Defense-in-Depth gegen
        // fehlerhafte Actions, nicht nur gegen den einen behobenen Fall.
        ctx._overlayConsumedMouseDown = true
        // Grayed Item (null) → Menü bleibt offen
        if (result !== null) {
            ctx.contextMenu.close()
            ctx.screen.markDirty()
            // Menü kann Border-Zellen überdecken — Border neu zeichnen lassen,
            // sonst bleiben Pixel-Reste auf dem Pane-Rahmen stehen.
            ctx.layout.borderDirty = true
        }
        if (result && result.action) {
            dispatchContextAction(result.action)
        }
        return  // Klick NICHT weitergeben
    }

    // Spellcheck-Menu hat Prioritaet wenn offen — selbe Semantik wie ContextMenu
    if (ctx.spellCheckMenu && ctx.spellCheckMenu.isOpen) {
        ctx.spellCheckMenu.handleClick(charX, charY)
        ctx.screen.markDirty()
        ctx.layout.borderDirty = true
        ctx._overlayConsumedMouseDown = true
        return
    }

    // UserMenu offen → Klick verarbeiten oder schliessen
    if (ctx.userMenu && ctx.userMenu.isOpen) {
        ctx.userMenu.handleClick(charX, charY)
        ctx.screen.markDirty()
        ctx._overlayConsumedMouseDown = true
        return
    }

    // Menu (Command Palette) offen → mousedown blockieren, click handled es
    if (ctx.menu.isMenuOpen()) {
        ctx.menu.handleClick(charX, charY)
        ctx.screen.markDirty()
        ctx._overlayConsumedMouseDown = true
        return
    }

    // Klick bei offener Suche: ausserhalb des Popups schliesst die Suche
    if (ctx.searchManager && ctx.searchManager.isSearchOpen()) {
        const sx = ctx.searchManager.popupX
        const sy = ctx.searchManager.popupY
        const sw = ctx.searchManager.popupWidth
        const sh = 3
        const insidePopup = charX >= sx && charX < sx + sw && charY >= sy && charY < sy + sh
        if (!insidePopup) {
            // Navigation-History registrieren (wie bei ESC)
            if (ctx._searchHistoryFromPos) {
                const toPos = ctx.editor.input._getCurrentHistoryPos()
                if (toPos && (toPos.paragraphId !== ctx._searchHistoryFromPos.paragraphId
                    || toPos.offset !== ctx._searchHistoryFromPos.offset
                    || toPos.field !== ctx._searchHistoryFromPos.field)) {
                    ctx.editor.input.navigationHistory.navigate(ctx._searchHistoryFromPos, toPos)
                }
                ctx._searchHistoryFromPos = null
            }
            ctx.searchManager.close()
            ctx.editor.view.searchHighlightFn = null
            ctx.layout.isDirty = true
            // Klick schließt Suche — NICHT weitergeben (wie ESC)
            ctx._overlayConsumedMouseDown = true
            return
        } else {
            return  // Klick im Popup: ignorieren
        }
    }

    // Link-Picker: Klick auf Item → akzeptieren, Klick ausserhalb → schließen
    if (ctx.editor && ctx.editor.input.getLinkPickerState()) {
        if (ctx.editor.input.handleLinkPickerClick(charX, charY)) {
            // Picker hat den Klick konsumiert (Item akzeptiert oder Klick im
            // Rahmen). Das nachfolgende `click`-Event darf NICHT zusaetzlich
            // an den Editor gehen — handleClick() hat keinen Picker-Zweig und
            // wuerde sonst den Cursor neben den Link springen lassen.
            ctx._overlayConsumedMouseDown = true
            return  // Klick vom Picker konsumiert
        }
        // handleLinkPickerClick gab false zurueck → Picker hat sich per
        // cancel() geschlossen (Klick ausserhalb). Klick faellt bewusst durch,
        // damit er den Cursor an der Klick-Position positioniert — kein Flag.
    }

    // Scrollbar-Klick auf dem zuständigen Pane (unabhängig vom aktiven Pane).
    // getActiveScrollSource liefert den richtigen ProseEditor — bei FULLSCREEN-
    // Memory/PersonalDict deren eigene Instanz, sonst den Haupt-Editor.
    const scrollSource = getActiveScrollSource(ctx)
    if (scrollSource) {
        const ib = scrollSource.pane.innerBounds
        const sbX = ib.x + ib.width - 1
        if (charX === sbX && charY >= ib.y && charY < ib.y + ib.height) {
            // Koordinaten so transformieren, dass EditorInput die Scrollbar erkennt
            const scrollbarX = scrollSource.editor.view.width  // isOnScrollbar(x) prüft x === this.width
            const localY = charY - ib.y
            const fractionalLocalY = fractionalCharY - ib.y
            scrollSource.editor.scrollbarViewHeight = ib.height
            scrollSource.editor.handleMouseDown(scrollbarX, localY, e.shiftKey, fractionalLocalY)
            return
        }
    }

    // Memory-Editor offen: Drag-Selektion an Memory-ProseEditor delegieren
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()
        && ctx.memoryEditor.proseEditor
    ) {
        const memPane = ctx.layout.panes[PaneId.MEMORY]
        if (memPane && memPane.isVisible()) {
            const ib = memPane.innerBounds
            if (charX >= ib.x && charX < ib.x + ib.width
                && charY >= ib.y && charY < ib.y + ib.height
            ) {
                const lc = _localCoords(memPane, charX, charY, fractionalCharY, pixelX)
                ctx.memoryEditor.proseEditor.handleMouseDown(
                    lc.x, lc.y, e.shiftKey, lc.y, lc.pixelX
                )
                return
            }
        }
    }

    // Personal-Dict-Editor offen: Drag-Selektion an Dict-ProseEditor delegieren
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
        && ctx.personalDictEditor.proseEditor
    ) {
        const dictPane = ctx.layout.panes[PaneId.PERSONAL_DICT]
        if (dictPane && dictPane.isVisible()) {
            const ib = dictPane.innerBounds
            if (charX >= ib.x && charX < ib.x + ib.width
                && charY >= ib.y && charY < ib.y + ib.height
            ) {
                const lc = _localCoords(dictPane, charX, charY, fractionalCharY, pixelX)
                ctx.personalDictEditor.proseEditor.handleMouseDown(
                    lc.x, lc.y, e.shiftKey, lc.y, lc.pixelX
                )
                return
            }
        }
    }

    // Editor-Interaktion bei Klick im Main-Inhaltsbereich
    // Impliziter Pane-Wechsel bei Mousedown ermöglicht sofortiges Drag-Selektieren
    const mainPane = ctx.layout.panes[PaneId.MAIN]
    if (mainPane && mainPane.isExpanded()) {
        const ib = mainPane.innerBounds
        if (charX >= ib.x && charX < ib.x + ib.width &&
            charY >= ib.y && charY < ib.y + ib.height) {
            // Pane aktivieren falls nötig (ohne ensureCursorVisible)
            if (ctx.layout.activePane !== PaneId.MAIN) {
                ctx.layout.setActivePane(PaneId.MAIN)
                ctx.layout.exitNavigationMode()
                ctx.layoutNavigationMode = false
                ctx.editor.switchField('text')
                saveSettings()
            }
            const lc = _localCoords(mainPane, charX, charY, fractionalCharY, pixelX)
            ctx.editor.handleMouseDown(lc.x, lc.y, e.shiftKey, lc.y, lc.pixelX)
            return
        }
    }

    // Mousedown im Outline-Pane → Note-Drag starten
    const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
    if (outlinePane && outlinePane.isExpanded()) {
        const ib = outlinePane.innerBounds
        if (charX >= ib.x && charX < ib.x + ib.width &&
            charY >= ib.y && charY < ib.y + ib.height) {
            const lc = _localCoords(outlinePane, charX, charY, fractionalCharY, pixelX)
            // Separator-Klick bei sichtbarer Main: Controls durchlassen, Rest blockieren
            const mainPaneMD = ctx.layout.panes[PaneId.MAIN]
            const mainVisibleMD = mainPaneMD && mainPaneMD.isVisible() && mainPaneMD.innerBounds.width > 0
            if (mainVisibleMD && ctx.editor.isSeparatorAtY(lc.y)) {
                // Controls (<|>|=) muessen auch bei sichtbarer Main funktionieren
                const sepControl = ctx.editor.outlineView &&
                    ctx.editor.outlineView.getSeparatorControlAtPosition(lc.x, lc.y)
                if (sepControl) {
                    ctx.editor._handleSeparatorControl(sepControl)
                    return
                }
                ctx.editor.input._pingBlocked()
                return
            }
            if (ctx.layout.activePane !== PaneId.OUTLINE) {
                ctx.layout.setActivePane(PaneId.OUTLINE)
                ctx.layout.exitNavigationMode()
                ctx.layoutNavigationMode = false
                saveSettings()
            }
            ctx.editor.handleNoteMouseDown(lc.x, lc.y, e.shiftKey, lc.pixelX)
        }
    }

    // Mousedown im Context-Pane
    const contextPaneMD = ctx.layout.panes[PaneId.CONTEXT]
    if (ctx.editor && contextPaneMD && contextPaneMD.isExpanded()) {
        const ib = contextPaneMD.innerBounds
        if (charX >= ib.x && charX < ib.x + ib.width &&
            charY >= ib.y && charY < ib.y + ib.height) {
            const lc = _localCoords(contextPaneMD, charX, charY, fractionalCharY, pixelX)
            // Separator-Klick bei sichtbarer Main oder Outline: blockieren + Ping
            const mainPaneMD = ctx.layout.panes[PaneId.MAIN]
            const mainVisibleMD = mainPaneMD && mainPaneMD.isVisible() && mainPaneMD.innerBounds.width > 0
            const outVisibleMD = outlinePane && outlinePane.isVisible() && outlinePane.innerBounds.width > 0
            if ((mainVisibleMD || outVisibleMD) && ctx.editor.isSeparatorAtY(lc.y)) {
                ctx.editor.input._pingBlocked()
                return
            }
            if (ctx.layout.activePane !== PaneId.CONTEXT) {
                ctx.layout.setActivePane(PaneId.CONTEXT)
                ctx.layout.exitNavigationMode()
                ctx.layoutNavigationMode = false
                saveSettings()
            }
            ctx.editor.switchField('info')
            ctx.editor.handleInfoMouseDown(lc.x, lc.y, e.shiftKey, lc.pixelX)
        }
    }

    // Mousedown im Core-Pane
    if (ctx.coreConsole) {
        const corePane = ctx.layout.panes[PaneId.CORE]
        if (corePane && corePane.isExpanded()) {
            const ib = corePane.innerBounds
            if (charX >= ib.x && charX < ib.x + ib.width &&
                charY >= ib.y && charY < ib.y + ib.height) {
                // Klick fokussiert die Console sofort (aktiver Border)
                if (!ctx.layout.isConsoleFocused()) {
                    ctx.layout.setConsoleFocus(ConsoleFocus.FOCUSED)
                    ctx.layout.exitNavigationMode()
                    ctx.layoutNavigationMode = false
                }
                const lc = _localCoords(corePane, charX, charY, fractionalCharY, pixelX, pixelY)
                ctx.coreConsole.handleMouseDown(lc.x, lc.intY, e, lc.pixelX, lc.pixelY, lc.y)
                ctx.layout.isDirty = true
            }
        }
    }
}

/**
 * Behandelt Mouse-Up Events (Ende von Drag-Selection).
 *
 * @param {MouseEvent} e
 */
export function handleMouseUp(e) {
    // Project Dashboard offen (Stufe E Drag&Drop): mouseup committet einen
    // laufenden Drop bzw. cancelt das pre-drag candidate-State. Dialog
    // hat Vorrang (analog zum mousedown-Pfad).
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen
            && !(ctx.dialogManager && ctx.dialogManager.isDialogOpen())) {
        const { charX, charY } = ctx.renderer.getCharPosition(e)
        // Fire-and-forget — handleMouseUp ist async, aber wir warten nicht;
        // der nachfolgende click-Event wird vom justFinishedDrag-Flag im
        // Dashboard verschluckt.
        ctx.projectDashboard.handleMouseUp(charX, charY)
        return
    }

    // Memory-Editor: Mouse-Up an den Memory-ProseEditor delegieren, damit
    // EditorInput.justFinishedDrag korrekt gesetzt wird — sonst verwirft der
    // nachfolgende `click`-Event die frisch aufgezogene Selection.
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()
        && ctx.memoryEditor.proseEditor
    ) {
        const memPane = ctx.layout.panes[PaneId.MEMORY]
        if (memPane && memPane.isVisible()) {
            const { charX, charY } = ctx.renderer.getCharPosition(e)
            const localX = charX - memPane.innerBounds.x
            const localY = charY - memPane.innerBounds.y
            ctx.memoryEditor.proseEditor.handleMouseUp(localX, localY)
        }
        return
    }

    // Personal-Dict-Editor: analog Memory — Mouse-Up an Dict-ProseEditor
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
        && ctx.personalDictEditor.proseEditor
    ) {
        const dictPane = ctx.layout.panes[PaneId.PERSONAL_DICT]
        if (dictPane && dictPane.isVisible()) {
            const { charX, charY } = ctx.renderer.getCharPosition(e)
            const localX = charX - dictPane.innerBounds.x
            const localY = charY - dictPane.innerBounds.y
            ctx.personalDictEditor.proseEditor.handleMouseUp(localX, localY)
        }
        return
    }

    if (!ctx.editor) return

    // Scrollbar-Drag beenden — unabhängig vom aktiven Pane. Bei FULLSCREEN-
    // Memory/PersonalDict-Editoren ist die Drag-State im jeweiligen Pane-
    // Editor, nicht im Main-Editor — getActiveScrollSource liefert die
    // richtige Instanz.
    const scrollSource = getActiveScrollSource(ctx)
    if (scrollSource && scrollSource.editor.isScrollbarDragging) {
        const { charX, charY } = ctx.renderer.getCharPosition(e)
        const ib = scrollSource.pane.innerBounds
        const localX = charX - ib.x
        const localY = charY - ib.y
        scrollSource.editor.handleMouseUp(localX, localY)
        return
    }

    // Core-Scrollbar-Drag beenden (auch wenn Maus außerhalb Core-Pane)
    if (ctx.coreConsole && ctx.coreConsole.isScrollbarDragging) {
        ctx.coreConsole.handleMouseUp()
        return
    }

    // Output-Drag beenden — unabhaengig davon, ob die Maus im Pane ist
    // (Maus kann beim Loslassen oben/unten ueber dem Pane-Rand sein)
    if (ctx.coreConsole && ctx.coreConsole.isOutputDragging()) {
        ctx.coreConsole.handleMouseUp()
        return
    }

    if (ctx.layout.activePane === PaneId.MAIN) {
        const { charX, charY } = ctx.renderer.getCharPosition(e)
        const mainPane = ctx.layout.panes[PaneId.MAIN]
        if (mainPane && mainPane.isExpanded()) {
            const localX = charX - mainPane.innerBounds.x
            const localY = charY - mainPane.innerBounds.y
            ctx.editor.handleMouseUp(localX, localY)
        }
    } else if (ctx.layout.activePane === PaneId.OUTLINE) {
        ctx.editor.handleNoteMouseUp()
    } else if (ctx.layout.activePane === PaneId.CONTEXT && ctx.editor) {
        ctx.editor.handleInfoMouseUp()
    } else if (ctx.layout.isConsoleActive() && ctx.coreConsole) {
        ctx.coreConsole.handleMouseUp()
    }
}

/**
 * Behandelt Mouse-Wheel Events (Scrollen).
 *
 * @param {WheelEvent} e
 */
export function handleMouseWheel(e) {
    if (!ctx.editor) return

    // Link-Picker offen → Mausrad scrollt die Item-Liste zeilenweise. Muss VOR
    // dem overlay.isActive-Early-Return stehen: der Picker lebt im Overlay,
    // sonst wuerde das Wheel-Event verschluckt. Liegt die Maus nicht ueber dem
    // Picker, wird das Event trotzdem konsumiert (kein Editor-Scroll dahinter).
    if (ctx.editor.input.getLinkPickerState()) {
        e.preventDefault()
        const { charX, charY } = ctx.renderer.getCharPosition(e)
        const b = ctx.editor.input._linkPickerBounds
        if (b && charX >= b.x && charX < b.x + b.width
            && charY >= b.y && charY < b.y + b.height) {
            // Gedrosselte Sensitivity: pro Notch nur eine Teil-Zeile, der
            // LinkPicker akkumuliert bis zur vollen Zeile (sonst zu schnell).
            const lines = (e.deltaY < 0 ? -1 : 1) * EDITOR.LINK_PICKER_WHEEL_SENSITIVITY
            if (ctx.editor.input.handleLinkPickerWheel(lines)) {
                ctx.editor.view.markDirty()
            }
        }
        return
    }

    // Project Dashboard offen → Mausrad scrollt die Projektliste. Selbe
    // Drosselung wie der LinkPicker (Wheel-Accumulator im Dashboard).
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen) {
        e.preventDefault()
        const lines = (e.deltaY < 0 ? -1 : 1) * EDITOR.LINK_PICKER_WHEEL_SENSITIVITY
        ctx.projectDashboard.handleWheel(lines)
        return
    }

    if (ctx.overlay.isActive) return

    // Event-Latency messen: Differenz zwischen Event-Entstehung (vom Browser gestempelt)
    // und dem Moment, wo unser Handler dran ist. Hohe Werte deuten auf Browser-Batching
    // / Compositor-Stau hin (Safari staut non-passive Wheel-Events gerne gebündelt aus).
    const handlerStart = performance.now()
    const eventLag = (typeof e.timeStamp === 'number') ? handlerStart - e.timeStamp : 0

    // Trackpad-Pinch (macOS/iPadOS) erscheint als Wheel-Event mit ctrlKey.
    // Auf unseren Font-Zoom umbiegen, damit das Browser-Layout nicht "kaputt"
    // skaliert wird.
    if (e.ctrlKey) {
        e.preventDefault()
        handleZoom(e.deltaY < 0 ? 'in' : 'out')
        return
    }

    const { charX, charY } = ctx.renderer.getCharPosition(e)

    // Memory/PersonalDict im FULLSCREEN: Mausrad an deren ProseEditor delegieren.
    // Muss VOR den Standard-Panes stehen, weil im FULLSCREEN-Modus MAIN/OUTLINE/
    // CONTEXT unsichtbar sind und der Editor-Loop unten ohnehin nichts findet —
    // aber der Helper liefert den richtigen ProseEditor mit eigener View-State.
    for (const paneEditor of [ctx.memoryEditor, ctx.personalDictEditor]) {
        if (!paneEditor || !paneEditor.isActive() || !paneEditor.proseEditor) continue
        const paneId = paneEditor === ctx.memoryEditor ? PaneId.MEMORY : PaneId.PERSONAL_DICT
        const pane = ctx.layout.panes[paneId]
        if (!pane || !pane.isVisible()) continue
        const b = pane.bounds
        if (charX >= b.x && charX < b.x + b.width && charY >= b.y && charY < b.y + b.height) {
            e.preventDefault()
            paneEditor.proseEditor.handleMouseWheel(e.deltaY, e.deltaMode)
            ctx.layout.isDirty = true
            scrollProfiler.noteWheel(eventLag, performance.now() - handlerStart)
            return
        }
    }

    // Scrollen wenn Maus über einem der drei oberen Panes liegt
    for (const paneId of [PaneId.OUTLINE, PaneId.MAIN, PaneId.CONTEXT]) {
        const pane = ctx.layout.panes[paneId]
        if (pane && pane.isVisible()) {
            const b = pane.bounds
            if (charX >= b.x && charX < b.x + b.width && charY >= b.y && charY < b.y + b.height) {
                e.preventDefault()
                ctx.editor.handleMouseWheel(e.deltaY, e.deltaMode)
                ctx.layout.isDirty = true
                scrollProfiler.noteWheel(eventLag, performance.now() - handlerStart)
                return
            }
        }
    }

    // Scrollen wenn Maus über Core-Pane liegt
    if (ctx.coreConsole) {
        const corePane = ctx.layout.panes[PaneId.CORE]
        if (corePane && corePane.isVisible()) {
            const b = corePane.bounds
            if (charX >= b.x && charX < b.x + b.width && charY >= b.y && charY < b.y + b.height) {
                e.preventDefault()
                ctx.coreConsole.handleWheel(e.deltaY, e.deltaMode)
                ctx.layout.isDirty = true
                scrollProfiler.noteWheel(eventLag, performance.now() - handlerStart)
                return
            }
        }
    }
}

/**
 * Behandelt Maus-Klicks auf dem Canvas.
 *
 * @param {MouseEvent} e
 * @param {{touchSlop?: boolean}} [opts] - touchSlop=true aktiviert Slop-toleranten
 *   Hit-Test fuer Border-Controls (Touch-Tap-Pfad).
 */
export async function handleClick(e, opts = {}) {
    // Synthetischen iOS-Click nach Long-Press einmalig verschlucken — sonst
    // wuerde der Click auf die Long-Press-Position das gerade geoeffnete
    // Context-Menu sofort wieder schliessen.
    if (performance.now() < _suppressNextClickUntil) {
        _suppressNextClickUntil = 0
        return
    }

    // Overlay hat mousedown konsumiert → click nicht weitergeben
    if (ctx._overlayConsumedMouseDown) {
        ctx._overlayConsumedMouseDown = false
        return
    }

    const { charX, charY, pixelX, fractionalCharY } = ctx.renderer.getCharPosition(e)

    // Scrollbar-Klick auf dem zuständigen Pane soll kein Pane-Wechsel auslösen.
    // getActiveScrollSource liefert auch im FULLSCREEN-Modus den richtigen Pane.
    const sbSource = getActiveScrollSource(ctx)
    if (sbSource) {
        const ib = sbSource.pane.innerBounds
        const sbX = ib.x + ib.width - 1
        if (charX === sbX && charY >= ib.y && charY < ib.y + ib.height) {
            return
        }
    }

    // Touch-Pfad: Tab-Leiste einblenden bei Tap auf y=0/y=1, wenn kein
    // Border-Control dort liegt. Wirkt wie der Maus-Hover-Trigger weiter unten,
    // nur mit Tap statt Hover.
    if (opts.touchSlop && ctx.tabManager && ctx.tabManager.openTabs.length > 0
        && !ctx.layout.tabBarVisible
        && charY >= 0 && charY < TOUCH.tabBarTrigger.height) {
        const ctrlSlop = ctx.renderer.isProportional()
            ? TOUCH.slop.borderControlProp
            : TOUCH.slop.borderControl
        const conflict = ctx.layout.getControlAtPosition(charX, charY, ctrlSlop)
        if (!conflict) {
            ctx.layout.showTabBar()
            _resizeAfterTabBarChange()
            return
        }
        // Border-Control hat Vorrang — der Hit-Test weiter unten verarbeitet ihn
    }

    // Tab-Leiste: Klick auf Tabs, Close, Control, Scroll, Username
    if (ctx.layout.tabBarVisible && charY === 0 && ctx.tabBarRenderer) {
        const tbSlop = opts.touchSlop ? TOUCH.slop.tabBar.dx : 0
        const element = ctx.tabBarRenderer.getElementAtPosition(charX, ctx.layout.cols, tbSlop)
        if (element) {
            if (element.type === 'tab') {
                // Memory-Editor: Tab-Klick schliesst ihn (Spec §2 close())
                if (ctx.memoryEditor && ctx.memoryEditor.isActive()) {
                    await ctx.memoryEditor.close()
                }
                await switchToProject(element.projectId)
                ctx.tabManager.setActiveTab(element.projectId)
            } else if (element.type === 'close') {
                const newActive = ctx.tabManager.closeTab(element.projectId)
                if (newActive === null) {
                    // Letzter Tab geschlossen — Dashboard oeffnen
                    if (ctx.projectDashboard) {
                        ctx.projectDashboard.open()
                    }
                } else if (newActive !== ctx.currentProjectId) {
                    await switchToProject(newActive)
                }
                ctx.renderer.markBorderDirty()
                ctx.layout.isDirty = true
            } else if (element.type === 'control') {
                // Tab-Leiste vor dem Menue-Open ausblenden — sonst lebt die
                // Tab-Leiste oben weiter, waehrend das Menue direkt darunter
                // erscheint, und der obere Rand wirkt unruhig.
                if (ctx.layout.tabBarVisible) {
                    ctx.layout.hideTabBar()
                    ctx.tabBarRenderer.clearHover()
                    _resizeAfterTabBarChange()
                }
                ctx.menu.open()
            } else if (element.type === 'scroll-left') {
                ctx.tabBarRenderer.scrollLeft()
                ctx.renderer.markBorderDirty()
                ctx.layout.isDirty = true
            } else if (element.type === 'scroll-right') {
                ctx.tabBarRenderer.scrollRight()
                ctx.renderer.markBorderDirty()
                ctx.layout.isDirty = true
            } else if (element.type === 'username' && ctx.userMenu) {
                if (ctx.userMenu.isOpen) {
                    ctx.userMenu.close()
                } else if (ctx.user) {
                    // Tab-Leiste vor dem Menue-Open ausblenden — analog zum
                    // Control-Klick (siehe oben), damit oben nicht beide
                    // Layer gleichzeitig sichtbar sind.
                    if (ctx.layout.tabBarVisible) {
                        ctx.layout.hideTabBar()
                        ctx.tabBarRenderer.clearHover()
                        _resizeAfterTabBarChange()
                    }
                    ctx.userMenu.show(ctx.user, ctx.layout.cols)
                }
                ctx.screen.markDirty()
            }
        }
        return
    }

    // UserMenu hat Priorität wenn offen
    if (ctx.userMenu && ctx.userMenu.isOpen) {
        ctx.userMenu.handleClick(charX, charY)
        ctx.screen.markDirty()
        return
    }

    // Dialog hat hoechste Prioritaet — sitzt visuell oben, kann auch aus
    // dem Dashboard heraus geoeffnet sein. MUSS vor dem Dashboard geprueft
    // werden, sonst frisst der Dashboard-Klick-Pfad das Klick-Event und
    // das Dialog-Promise bleibt pending (Dialog erscheint dann bei jedem
    // Re-Render wieder).
    if (ctx.dialogManager && ctx.dialogManager.isDialogOpen()) {
        ctx.dialogManager.handleClick(charX, charY)
        ctx.screen.markDirty()
        return
    }

    // Dashboard hat Prioritaet wenn offen
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen) {
        ctx.projectDashboard.handleClick(charX, charY)
        ctx.screen.markDirty()
        return
    }

    // Menü hat Priorität wenn offen
    if (ctx.menu.isMenuOpen()) {
        ctx.menu.handleClick(charX, charY)
        ctx.screen.markDirty()
        return
    }

    // Layout-Modus bei Klick verlassen
    ctx.layoutNavigationMode = false

    // Control geklickt? Bei Touch-Tap mit Slop, sonst pixelgenau (Maus).
    const ctrlSlop = opts.touchSlop
        ? (ctx.renderer.isProportional() ? TOUCH.slop.borderControlProp : TOUCH.slop.borderControl)
        : null
    const control = ctx.layout.getControlAtPosition(charX, charY, ctrlSlop)
    if (control) {
        // Memory-Editor: (-) auf der Memory-Pane schliesst den Editor
        // (Spec §6 Buttons), nicht das normale Pane-Collapse-Handling
        if (ctx.memoryEditor && ctx.memoryEditor.isActive()
            && control.paneId === PaneId.MEMORY
            && control.type === 'COLLAPSE'
        ) {
            await ctx.memoryEditor.close()
            ctx.layout.isDirty = true
            return
        }
        // Analog Personal-Dict (Spellcheck §10 Phase 4)
        if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
            && control.paneId === PaneId.PERSONAL_DICT
            && control.type === 'COLLAPSE'
        ) {
            await ctx.personalDictEditor.close()
            ctx.layout.isDirty = true
            return
        }
        ctx.layout.handleControlClick(control)
        // Cursor-Position im (evtl. neu aktivierten) Pane validieren —
        // Expand/Collapse kann activePane verschieben (z.B. Maximize Outline
        // setzt activePane=OUTLINE, Cursor muss dann ins note-Feld und auf
        // einen sichtbaren Absatz)
        activatePane(ctx.layout.activePane)
        saveSettings()
        return
    }

    // Klick auf Pane-Titelzeile → zum aktuellen Abschnitt navigieren (alle drei Panes)
    if (ctx.editor) {
        const editorPanes = [PaneId.OUTLINE, PaneId.MAIN, PaneId.CONTEXT]
        for (const paneId of editorPanes) {
            const pane = ctx.layout.panes[paneId]
            if (pane && pane.isExpanded() && charY === pane.bounds.y) {
                const b = pane.bounds
                if (charX > b.x && charX < b.x + b.width - 1) {
                    const section = ctx.editor.view.getSectionAboveViewport()
                    if (section) {
                        ctx.editor.view.scrollTo(section.firstContentLineIndex)
                    } else {
                        ctx.editor.view.scrollTo(0)
                    }
                    ctx.editor.view.markDirty()
                    ctx.layout.isDirty = true
                    return
                }
            }
        }
    }

    // Memory-Editor offen: Klick an den Memory-ProseEditor delegieren statt
    // an den Haupt-Editor (Spec §6 Phase 4)
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()) {
        const memPane = ctx.layout.panes[PaneId.MEMORY]
        if (memPane && memPane.isVisible() && ctx.memoryEditor.proseEditor) {
            const lc = _localCoords(memPane, charX, charY, fractionalCharY, pixelX)
            ctx.memoryEditor.proseEditor.handleClick(lc.x, lc.y, e.shiftKey, lc.pixelX)
            ctx.layout.isDirty = true
        }
        return
    }

    // Personal-Dict-Editor: Klick analog Memory delegieren
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()) {
        const dictPane = ctx.layout.panes[PaneId.PERSONAL_DICT]
        if (dictPane && dictPane.isVisible() && ctx.personalDictEditor.proseEditor) {
            const lc = _localCoords(dictPane, charX, charY, fractionalCharY, pixelX)
            ctx.personalDictEditor.proseEditor.handleClick(lc.x, lc.y, e.shiftKey, lc.pixelX)
            ctx.layout.isDirty = true
        }
        return
    }

    // Bereich geklickt?
    const pane = ctx.layout.getPaneAtPosition(charX, charY)
    if (pane && pane.isExpanded()) {
        // Separator-Klick in Outline/Context bei sichtbarer Main/Outline: bereits in mousedown geblockt
        // Click-Handler muss konsistent sein und ebenfalls abbrechen
        // Ausnahme: Separator-Controls (<|>|=) muessen immer durchgelassen werden
        if (ctx.editor && (pane.id === PaneId.OUTLINE || pane.id === PaneId.CONTEXT)) {
            const lcSep = _localCoords(pane, charX, charY, fractionalCharY, pixelX)
            if (ctx.editor.isSeparatorAtY(lcSep.y)) {
                const mainP = ctx.layout.panes[PaneId.MAIN]
                const mainVis = mainP && mainP.isVisible() && mainP.innerBounds.width > 0
                if (pane.id === PaneId.OUTLINE && mainVis) {
                    // Controls wurden bereits in mousedown behandelt
                    return
                }
                if (pane.id === PaneId.CONTEXT) {
                    const outP = ctx.layout.panes[PaneId.OUTLINE]
                    const outVis = outP && outP.isVisible() && outP.innerBounds.width > 0
                    if (mainVis || outVis) return
                }
            }
        }

        // Bereich aktivieren und Cursor auf Cursor-Position setzen
        ctx.layout.setActivePane(pane.id)
        ctx.layout.exitNavigationMode()  // Setzt currentStation auf Cursor des aktiven Bereichs
        saveSettings()

        // Klick an Editor weiterleiten wenn Main-Pane
        if (pane.id === PaneId.MAIN && ctx.editor) {
            ctx.editor.switchField('text')
            const lc = _localCoords(pane, charX, charY, fractionalCharY, pixelX)
            ctx.editor.handleClick(lc.x, lc.y, e.shiftKey, lc.pixelX, { touchSlop: !!opts.touchSlop })
            ctx.layout.isDirty = true
        }

        // Klick im Outline-Pane → Note-Editing oder Separator-Controls
        if (pane.id === PaneId.OUTLINE && ctx.editor) {
            const lc = _localCoords(pane, charX, charY, fractionalCharY, pixelX)
            const result = ctx.editor.handleNoteClick(lc.x, lc.y, lc.pixelX)
            // Outline-Subtree-Selection → Main aktivieren (Cursor ist auf text-Feld)
            if (result === 'activateMain') {
                ctx.layout.setActivePane(PaneId.MAIN)
                ctx.layout.exitNavigationMode()
            }
            ctx.layout.isDirty = true
        }

        // Klick im Context-Pane → Info-Editing
        if (pane.id === PaneId.CONTEXT && ctx.editor) {
            const lc = _localCoords(pane, charX, charY, fractionalCharY, pixelX)
            ctx.editor.handleInfoClick(lc.x, lc.y, lc.pixelX)
            ctx.layout.isDirty = true
        }
    }
}

/**
 * Behandelt Maus-Bewegung für Hover-Effekte.
 *
 * @param {MouseEvent} e
 */
export function handleMouseMove(e) {
    // Mauscursor bei Bewegung wieder einblenden.
    // 'auto' statt 'default': bekannter Browser-Quirk — beim Uebergang
    // 'none' -> 'default' aktualisieren manche Browser den sichtbaren Cursor
    // erst beim naechsten Event. 'auto' triggert das Refresh zuverlaessiger.
    if (ctx.renderer.canvas.style.cursor === 'none') {
        ctx.renderer.canvas.style.cursor = 'auto'
    }

    const { charX, charY, pixelX, pixelY, fractionalCharY } = ctx.renderer.getCharPosition(e)

    // Dialog hat hoechste Prioritaet — sitzt visuell oben.
    if (ctx.dialogManager && ctx.dialogManager.isDialogOpen()) {
        const overButton = ctx.dialogManager.handleMouseMove(charX, charY)
        ctx.renderer.canvas.style.cursor = overButton ? 'pointer' : 'default'
        return
    }

    // Context Menu hat Prioritaet vor Dashboard — wenn ein Context-Menu
    // sichtbar ist, darf eine Maus-Bewegung ueber den Dashboard-Hover-Bereich
    // den Overlay nicht neu zeichnen (sonst Context-Menu verschwindet).
    if (ctx.contextMenu && ctx.contextMenu.isOpen) {
        if (ctx.contextMenu.handleMouseMove(charX, charY)) {
            ctx.screen.markDirty()
        }
        ctx.renderer.canvas.style.cursor = 'default'
        return
    }

    // Dashboard
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen) {
        ctx.projectDashboard.handleMouseMove(charX, charY)
        ctx.renderer.canvas.style.cursor = 'default'
        return
    }

    // Link-Picker offen → Hover-Selection wie die anderen Menues.
    if (ctx.editor && ctx.editor.input.getLinkPickerState()) {
        const overPicker = ctx.editor.input.handleLinkPickerMouseMove(charX, charY)
        const b = ctx.editor.input._linkPickerBounds
        // Pointer nur ueber den echten Item-Zeilen — nicht ueber Eingabezeile,
        // Trennlinie oder Rahmen.
        const overItem = overPicker && b
            && charY >= b.y + b.itemRowOffset
            && charY < b.y + b.itemRowOffset + b.maxVisible
        ctx.renderer.canvas.style.cursor = overItem ? 'pointer' : 'default'
        return
    }

    // Spellcheck-Menu offen → Hover-Selection wie das Hauptmenue.
    // Pointer-Cursor ueber selektierbaren Items (Suggestions + Aktionen).
    if (ctx.spellCheckMenu && ctx.spellCheckMenu.isOpen) {
        const overMenu = ctx.spellCheckMenu.handleMouseMove(charX, charY)
        const items = ctx.spellCheckMenu._items
        const itemIdx = charY - ctx.spellCheckMenu._menuY - 1
        const overSelectable = overMenu
            && itemIdx >= 0 && itemIdx < items.length
            && items[itemIdx].kind === 'item'
        ctx.renderer.canvas.style.cursor = overSelectable ? 'pointer' : 'default'
        return
    }

    // UserMenu hat Priorität wenn offen
    if (ctx.userMenu && ctx.userMenu.isOpen) {
        const overMenu = ctx.userMenu.handleMouseMove(charX, charY)
        const itemStart = ctx.userMenu._itemStartY
        const itemEnd = itemStart + ctx.userMenu._items.length
        const overItem = overMenu && charY >= itemStart && charY < itemEnd
        ctx.renderer.canvas.style.cursor = overItem ? 'pointer' : 'default'
        return
    }

    // Menü hat Priorität wenn offen
    if (ctx.menu.isMenuOpen()) {
        const overMenu = ctx.menu.handleMouseMove(charX, charY)
        // Pointer wenn über Menü-Items (nicht Rahmen), sonst default
        const b = ctx.menu.menuBounds
        const overItem = overMenu && b && charY > b.y && charY < b.y + b.height - 1
        ctx.renderer.canvas.style.cursor = overItem ? 'pointer' : 'default'
        return
    }

    // Tab-Leiste: Am oberen Rand einblenden, bei Verlassen ausblenden
    // Controls auf dem Pane-Top-Border haben Vorrang — Tab-Leiste darf Controls nicht verdecken
    if (ctx.tabManager && ctx.tabManager.openTabs.length > 0) {
        if (charY === 0 && !ctx.layout.tabBarVisible) {
            // Nur einblenden wenn kein Control an dieser Position liegt
            const controlAtMouse = ctx.layout.getControlAtPosition(charX, 0)
            if (!controlAtMouse) {
                ctx.layout.showTabBar()
                _resizeAfterTabBarChange()
            }
        } else if (charY > 0 && ctx.layout.tabBarVisible) {
            // Nicht ausblenden wenn ein Top-Border-Control bei y=1 liegt (Tab-Leiste sichtbar)
            const controlAtY1 = charY === 1 ? ctx.layout.getControlAtPosition(charX, 1) : null
            if (!controlAtY1) {
                ctx.layout.hideTabBar()
                if (ctx.tabBarRenderer) ctx.tabBarRenderer.clearHover()
                _resizeAfterTabBarChange()
            }
        }

        // Hover-Feedback in der Tab-Leiste
        if (ctx.layout.tabBarVisible && charY === 0 && ctx.tabBarRenderer) {
            ctx.tabBarRenderer.setHover(charX, ctx.layout.cols)
            if (ctx.tabBarRenderer.isDirty) {
                ctx.renderer.markBorderDirty()
                ctx.layout.isDirty = true
            }
            // Pointer-Cursor bei klickbaren Elementen
            const element = ctx.tabBarRenderer.getElementAtPosition(charX, ctx.layout.cols)
            ctx.renderer.canvas.style.cursor = element ? 'pointer' : 'default'
            return
        }
    }

    // Control unter dem Mauszeiger?
    const control = ctx.layout.getControlAtPosition(charX, charY)
    ctx.layout.setHoveredControl(control)

    // Separator-Control-Hover im Outline-Pane
    let sepControlHovered = false
    if (ctx.editor && !control) {
        const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
        if (outlinePane && outlinePane.isExpanded()) {
            const ib = outlinePane.innerBounds
            if (charX >= ib.x && charX < ib.x + ib.width &&
                charY >= ib.y && charY < ib.y + ib.height) {
                const localX = charX - ib.x
                const localY = fractionalCharY - ib.y
                const sepCtrl = ctx.editor.outlineView.getSeparatorControlAtPosition(localX, localY)
                let hoverChanged = ctx.editor.outlineView.setHoveredSeparatorControl(sepCtrl)
                if (sepCtrl) sepControlHovered = true
                // Collapse-Icon eines Outline-Items (nur wenn kein Separator-Control trifft)
                const iconPid = sepCtrl
                    ? null
                    : ctx.editor.outlineView.getActiveCollapseIconParagraphId(localX, localY)
                if (ctx.editor.outlineView.setHoveredOutlineItemIcon(iconPid)) hoverChanged = true
                if (iconPid !== null) sepControlHovered = true
                // Hover-Wechsel braucht einen Content-Repaint des Outline-Panes.
                // borderDirty überspringt den Cursor-Only-Pfad (analog setHoveredControl),
                // sonst bliebe ein reiner Hover-Wechsel ohne sichtbares Repaint.
                if (hoverChanged) ctx.layout.borderDirty = true
            } else {
                const sepCleared = ctx.editor.outlineView.setHoveredSeparatorControl(null)
                const iconCleared = ctx.editor.outlineView.setHoveredOutlineItemIcon(null)
                if (sepCleared || iconCleared) ctx.layout.borderDirty = true
            }
        }
    }

    // Lock-Control-Hover auf einer Separator-Zeile im Main-Pane
    if (ctx.editor && !control) {
        const mainPane = ctx.layout.panes[PaneId.MAIN]
        if (mainPane && mainPane.isExpanded()) {
            const ib = mainPane.innerBounds
            let lockPid = null
            if (charX >= ib.x && charX < ib.x + ib.width &&
                charY >= ib.y && charY < ib.y + ib.height) {
                const localX = charX - ib.x
                const localY = fractionalCharY - ib.y
                const localPixelX = pixelX - ib.x * ctx.renderer.charWidth
                lockPid = ctx.editor.view.getMainSeparatorLockAtPosition(localX, localY, localPixelX)
            }
            if (ctx.editor.view.setHoveredMainSepLock(lockPid)) ctx.layout.borderDirty = true
            if (lockPid !== null) sepControlHovered = true
        }
    }

    // Outline-Titelzeile: Pointer-Cursor für klickbare Separator-Nummer
    let outlineTitleHovered = false
    if (ctx.editor && !control) {
        const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
        if (outlinePane && outlinePane.isExpanded() && charY === outlinePane.bounds.y) {
            const ob = outlinePane.bounds
            if (charX > ob.x && charX < ob.x + ob.width - 1) {
                outlineTitleHovered = true
            }
        }
    }

    // Link-Hover im Core-Pane (Pointer-Cursor fuer klickbare URLs)
    let coreLinkHovered = false
    if (ctx.coreConsole && !control) {
        const corePane = ctx.layout.panes[PaneId.CORE]
        if (corePane && corePane.isExpanded()) {
            const ib = corePane.innerBounds
            if (charX >= ib.x && charX < ib.x + ib.width &&
                charY >= ib.y && charY < ib.y + ib.height) {
                const localX = charX - ib.x
                const localY = charY - ib.y
                const localPixelX = pixelX - ib.x * ctx.renderer.charWidth
                const localPixelY = pixelY - ib.y * ctx.renderer.charHeight
                if (ctx.coreConsole.getLinkAtPosition(localX, localY, localPixelX, localPixelY)) {
                    coreLinkHovered = true
                }
            }
        }
    }

    // Link-Hover im Editor (Pointer-Cursor für klickbare Section-Links und Web-URLs)
    let editorLinkHovered = false
    if (ctx.editor && !control && !sepControlHovered && !outlineTitleHovered) {
        const linkPanes = [
            { paneId: PaneId.MAIN, field: 'text' },
            { paneId: PaneId.OUTLINE, field: 'note' },
            { paneId: PaneId.CONTEXT, field: 'info' }
        ]
        for (const { paneId, field } of linkPanes) {
            const pane = ctx.layout.panes[paneId]
            if (pane && pane.isExpanded()) {
                const ib = pane.innerBounds
                if (charX >= ib.x && charX < ib.x + ib.width &&
                    charY >= ib.y && charY < ib.y + ib.height) {
                    const localX = charX - ib.x
                    const localY = fractionalCharY - ib.y
                    const localPixelX = pixelX - ib.x * ctx.renderer.charWidth
                    if (ctx.editor.isLinkAtPosition(field, localX, localY, localPixelX)) {
                        editorLinkHovered = true
                        break
                    }
                }
            }
        }
    }

    // Cursor-Style anpassen
    ctx.renderer.canvas.style.cursor = (control || sepControlHovered || outlineTitleHovered || coreLinkHovered || editorLinkHovered) ? 'pointer' : 'default'

    // Console-Hover-Detection: Maus über Core-Pane?
    // Waehrend eines aktiven Output-Drags wird die Auto-Detection pausiert,
    // sonst wuerde das Verlassen der Pane-Kante den Console-Fokus killen
    // und damit den Drag-Dispatch unten (Zeile ~845) abreissen — der
    // Auto-Scroll triggert dann nie und der Drag "klemmt".
    const corePaneHover = ctx.layout.panes[PaneId.CORE]
    const outputDragging = ctx.coreConsole && ctx.coreConsole.isOutputDragging()
    if (corePaneHover && corePaneHover.isExpanded() && !outputDragging) {
        const cb = corePaneHover.bounds
        const mouseOverCore = charX >= cb.x && charX < cb.x + cb.width &&
            charY >= cb.y && charY < cb.y + cb.height
        if (mouseOverCore) {
            // Core im Fullscreen → immer FOCUSED
            if (corePaneHover.state === PaneState.FULLSCREEN) {
                if (!ctx.layout.isConsoleFocused()) ctx.layout.setConsoleFocus(ConsoleFocus.FOCUSED)
            } else if (!ctx.layout.isConsoleActive()) {
                ctx.layout.setConsoleFocus(ConsoleFocus.HOVER)
            }
        } else if (ctx.layout.isConsoleActive()) {
            // Maus hat Core verlassen → Console deaktivieren
            ctx.layout.setConsoleFocus(ConsoleFocus.NONE)
        }
    }

    // Memory-Editor offen: Drag direkt an Memory-ProseEditor leiten,
    // ohne den Haupt-Editor anzufassen.
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()
        && ctx.memoryEditor.proseEditor && (e.buttons & 1)
    ) {
        const memPane = ctx.layout.panes[PaneId.MEMORY]
        if (memPane && memPane.isVisible()) {
            const lc = _localCoords(memPane, charX, charY, fractionalCharY, pixelX)
            ctx.memoryEditor.proseEditor.handleMouseMove(lc.x, lc.y, lc.y, lc.pixelX)
            ctx.layout.isDirty = true
        }
        return
    }

    // Personal-Dict-Editor analog Memory
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
        && ctx.personalDictEditor.proseEditor && (e.buttons & 1)
    ) {
        const dictPane = ctx.layout.panes[PaneId.PERSONAL_DICT]
        if (dictPane && dictPane.isVisible()) {
            const lc = _localCoords(dictPane, charX, charY, fractionalCharY, pixelX)
            ctx.personalDictEditor.proseEditor.handleMouseMove(lc.x, lc.y, lc.y, lc.pixelX)
            ctx.layout.isDirty = true
        }
        return
    }

    // Drag an Editor weiterleiten (nur wenn linke Maustaste gedrückt)
    if (ctx.editor && (e.buttons & 1)) {
        // Scrollbar-Drag läuft — Koordinaten relativ zum Scrollbar-Pane
        if (ctx.editor.isScrollbarDragging) {
            const sbPane = getScrollbarPane(ctx.layout)
            if (sbPane) {
                const lc = _localCoords(sbPane, charX, charY, fractionalCharY, pixelX)
                ctx.editor.handleMouseMove(lc.x, lc.intY, lc.y)
            }
        } else if (ctx.layout.activePane === PaneId.MAIN) {
            const mainPane = ctx.layout.panes[PaneId.MAIN]
            if (mainPane && mainPane.isExpanded()) {
                const lc = _localCoords(mainPane, charX, charY, fractionalCharY, pixelX)
                ctx.editor.handleMouseMove(lc.x, lc.y, lc.y, lc.pixelX)
            }
        } else if (ctx.layout.activePane === PaneId.OUTLINE) {
            const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
            if (outlinePane && outlinePane.isExpanded()) {
                const lc = _localCoords(outlinePane, charX, charY, fractionalCharY, pixelX)
                ctx.editor.handleNoteMouseMove(lc.x, lc.y, lc.pixelX)
            }
        } else if (ctx.layout.activePane === PaneId.CONTEXT) {
            const ctxPaneMove = ctx.layout.panes[PaneId.CONTEXT]
            if (ctxPaneMove && ctxPaneMove.isExpanded()) {
                const lc = _localCoords(ctxPaneMove, charX, charY, fractionalCharY, pixelX)
                ctx.editor.handleInfoMouseMove(lc.x, lc.y, lc.pixelX)
            }
        } else if (ctx.coreConsole && ctx.coreConsole.isScrollbarDragging) {
            // Core-Scrollbar-Drag: Koordinaten relativ zum Core-Pane, auch wenn
            // die Maus den Pane verlassen hat (wie beim Editor-Scrollbar-Drag)
            const corePane = ctx.layout.panes[PaneId.CORE]
            if (corePane && corePane.isExpanded()) {
                const lc = _localCoords(corePane, charX, charY, fractionalCharY, pixelX, pixelY)
                ctx.coreConsole.handleMouseMove(lc.x, lc.intY, lc.pixelX, lc.pixelY, lc.y)
                ctx.layout.isDirty = true
            }
        } else if (ctx.coreConsole && ctx.coreConsole.isOutputDragging()) {
            // Output-Drag: mouseMove muss auch dann durchgereicht werden, wenn
            // die Maus den Core-Pane oben/unten verlaesst — sonst klemmt der
            // Auto-Scroll am Pane-Rand. lc.y wird im CoreInput.handleMouseMove
            // gegen `0` und `view.height` geprueft und triggert den Auto-Scroll.
            const corePane = ctx.layout.panes[PaneId.CORE]
            if (corePane && corePane.isExpanded()) {
                const lc = _localCoords(corePane, charX, charY, fractionalCharY, pixelX, pixelY)
                ctx.coreConsole.handleMouseMove(lc.x, lc.intY, lc.pixelX, lc.pixelY, lc.y)
                ctx.layout.isDirty = true
            }
        } else if (ctx.layout.isConsoleActive() && ctx.coreConsole) {
            const corePane = ctx.layout.panes[PaneId.CORE]
            if (corePane && corePane.isExpanded()) {
                const lc = _localCoords(corePane, charX, charY, fractionalCharY, pixelX, pixelY)
                ctx.coreConsole.handleMouseMove(lc.x, lc.intY, lc.pixelX, lc.pixelY, lc.y)
                ctx.layout.isDirty = true
            }
        }
    }
}

/**
 * Behandelt Rechtsklick — Context Menu auf Inhalt, Command Palette auf Border.
 *
 * @param {MouseEvent} e
 */
export function handleContextMenu(e) {
    e.preventDefault()

    const { charX, charY, pixelX, fractionalCharY } = ctx.renderer.getCharPosition(e)

    // Project Dashboard ist modal (Stufe G): Rechtsklick oeffnet ein
    // Dashboard-eigenes Context-Menu (Spec §5.5). Ausserhalb des Frames
    // schliesst Rechtsklick das Dashboard.
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen) {
        ctx.projectDashboard.handleContextMenu(charX, charY)
        ctx.screen.markDirty()
        return
    }

    // Border vs. Inhalt unterscheiden
    const pane = ctx.layout.getPaneAtPosition(charX, charY)
    if (!pane) {
        ctx.menu.open()
        ctx.screen.markDirty()
        return
    }

    const ib = pane.innerBounds
    const isOnContent = charX >= ib.x && charX < ib.x + ib.width &&
                        charY >= ib.y && charY < ib.y + ib.height

    if (!isOnContent) {
        ctx.menu.open()
        ctx.screen.markDirty()
        return
    }

    // Ohne Selection: Cursor an Klickposition setzen (Menü wirkt auf diese Stelle)
    // Mit Selection: Cursor/Selection beibehalten (Menü wirkt auf die Selection)
    const isConsole = pane.id === PaneId.CORE
    const isMemoryPane = pane.id === PaneId.MEMORY
    const isDictPane = pane.id === PaneId.PERSONAL_DICT
    // memoryProse deckt beide Plain-Text-Overlays ab — Variablen-Name historisch.
    const memoryProse = isMemoryPane && ctx.memoryEditor && ctx.memoryEditor.isActive()
        ? ctx.memoryEditor.proseEditor
        : isDictPane && ctx.personalDictEditor && ctx.personalDictEditor.isActive()
            ? ctx.personalDictEditor.proseEditor
            : null
    const hasInputSel = isConsole && ctx.coreConsole && ctx.coreConsole.model.hasSelection()
    const hasOutputSel = isConsole && ctx.coreConsole && ctx.coreConsole.model.hasOutputSelection()
    const hasSelection = isConsole
        ? (hasInputSel || hasOutputSel)
        : (isMemoryPane || isDictPane)
            ? (memoryProse && memoryProse.model.hasSelection())
            : (ctx.editor && ctx.editor.model.hasSelection())

    if (!hasSelection) {
        if (isConsole && ctx.coreConsole) {
            // Rechtsklick auf Output-Entry: spontane Wort-Selektion, damit das
            // Copy-Menu auf etwas Sinnvolles wirken kann (macOS-Verhalten).
            const lc = _localCoords(pane, charX, charY, fractionalCharY, pixelX)
            const view = ctx.coreConsole.view
            const inputOffset = view.screenToInput(lc.x, lc.intY, lc.pixelX)
            const entryInfo = inputOffset === null ? view.screenToEntry(lc.x, lc.intY, lc.pixelX) : null
            if (entryInfo && !entryInfo.inPrefix) {
                const entry = ctx.coreConsole.model.getEntries()[entryInfo.entryIndex]
                if (entry && !(entry.type === 'THINKING' && entry.collapsed)) {
                    ctx.coreConsole.input._selectWordInEntry(entryInfo)
                }
            } else {
                ctx.coreConsole.handleMouseDown(lc.x, lc.intY, e, lc.pixelX)
            }
        } else if (memoryProse) {
            const lc = _localCoords(pane, charX, charY, fractionalCharY, pixelX)
            memoryProse.handleClick(lc.x, lc.y, false, lc.pixelX)
        } else if (ctx.editor) {
            const lc = _localCoords(pane, charX, charY, fractionalCharY, pixelX)
            if (pane.id === PaneId.MAIN) {
                ctx.layout.setActivePane(PaneId.MAIN)
                ctx.editor.switchField('text')
                ctx.editor.handleClick(lc.x, lc.y, false, lc.pixelX)
            } else if (pane.id === PaneId.OUTLINE) {
                ctx.layout.setActivePane(PaneId.OUTLINE)
                ctx.editor.handleNoteClick(lc.x, lc.y, lc.pixelX)
            } else if (pane.id === PaneId.CONTEXT) {
                ctx.layout.setActivePane(PaneId.CONTEXT)
                ctx.editor.handleInfoClick(lc.x, lc.y, lc.pixelX)
            }
        }
    }

    // Spellcheck-Menue ANSTELLE des regulaeren ContextMenu, wenn der Klick
    // genau auf einem Wort mit Warning landet — oder die aktive Selection
    // genau ein solches Wort umspannt. Spec §7.3. Phase 2: nur Main-Pane
    // (text-Feld) — der SpellCheckManager prueft heute nur das text-Feld
    // (paragraphAdapter). Note/Info kommen mit Phase 3.
    if (_tryOpenSpellCheckMenu(pane, charX, charY, fractionalCharY, pixelX)) {
        ctx.screen.markDirty()
        return
    }

    // Context Menu öffnen
    const context = buildContextMenuContext(pane)
    const propInfo = ctx.renderer.isProportional() && ctx.renderer._metricsCache
        ? { metricsCache: ctx.renderer._metricsCache, scaleFactor: ctx.renderer.getScaleFactor(), charWidth: ctx.renderer.charWidth }
        : null
    ctx.contextMenu.open(charX, charY, context, propInfo)
    ctx.screen.markDirty()
}

/**
 * Prueft, ob die Klick-Position (oder eine genau-ein-Wort-Selektion) auf
 * einem Rechtschreib-Warning liegt. Wenn ja, oeffnet das SpellCheckMenu
 * und gibt true zurueck. Sonst false — der Caller faellt auf das regulaere
 * ContextMenu zurueck.
 *
 * Statt den Cursor zu lesen (der je nach hasSelection-Pfad vom Aufrufer
 * vor uns nicht garantiert auf die Klick-Position gesetzt wurde), nutzen
 * wir direkt `view.screenToData` mit den Klick-Koordinaten. Damit ist das
 * Routing unabhaengig vom Cursor-State. Im no-selection-Fall setzen wir
 * den Cursor abschliessend selbst, damit Replace + spaeteres Tippen im
 * richtigen Paragraph landet.
 *
 * @param {object} pane - Pane unter der Klickposition
 * @param {number} charX - Globale Char-Spalte (Anchor des Menues)
 * @param {number} charY - Globale Char-Zeile (Anchor des Menues)
 * @param {number} fractionalCharY
 * @param {number} pixelX
 * @returns {boolean}
 */
function _tryOpenSpellCheckMenu(pane, charX, charY, fractionalCharY, pixelX) {
    if (!ctx.spellCheckMenu || !ctx.spellCheckManager || !ctx.editor) return false
    if (!pane) return false
    const field = _spellCheckFieldFromPaneId(pane.id)
    if (!field) return false

    const view = ctx.editor.view
    const model = ctx.editor.model

    // Selection-Pfad hat Vorrang: wenn der User explizit ein Wort selektiert
    // hat (Doppelklick), bezieht sich das Menue auf diese Selektion — auch
    // wenn der Right-Click formal "neben" dem Wort landet.
    if (model.hasSelection()) {
        const sel = model.selection
        if (sel.anchor.paragraphId === sel.cursor.paragraphId
            && sel.anchor.field === sel.cursor.field
            && sel.anchor.field === field) {
            const para = model.getParagraph(sel.anchor.paragraphId)
            if (!para) return false
            const fieldText = field === 'text'
                ? (para.text || '')
                : (para[field] || '')
            const start = Math.min(sel.anchor.offset, sel.cursor.offset)
            const end = Math.max(sel.anchor.offset, sel.cursor.offset)
            if (start === end) return false  // entartete Selection — Klick-Pfad
            if (!isSingleWordSelection(fieldText, start, end)) return false
            const candidate = ctx.spellCheckManager.warningAt(sel.anchor.paragraphId, start, field)
            if (!candidate) return false
            const trimmedSlice = fieldText.slice(start, end).trim()
            // Trailing-Dot-Toleranz: Doppelklick markiert per Browser-Konvention
            // `bzw` ohne Punkt, die Warning umfasst aber `bzw.` (Tokenizer-
            // Pattern). Beide Varianten matchen.
            const sliceMatches = trimmedSlice === candidate.word
                || trimmedSlice + '.' === candidate.word
                || trimmedSlice === candidate.word + '.'
            if (!sliceMatches) return false
            ctx.spellCheckMenu.show(
                { warning: candidate, paragraphId: sel.anchor.paragraphId, field },
                charX, charY, ctx.screen.cols, ctx.screen.rows
            )
            return true
        }
        return false  // Multi-Para- oder cross-field-Selection → regulaer
    }

    // Klick-Position lokalisieren — Resolver pro Feld:
    //  - text (MAIN):   view.screenToData → {paragraphId, offset, field}
    //  - note (OUTLINE): editor._resolveNotePosition → {dataPos:{paragraphId, offset}, para}
    //  - info (CONTEXT): editor._resolveInfoPosition → {dataPos:{paragraphId, offset}, para}
    // screenToData ist Main-Pane-spezifisch; im Outline/Context-Pfad liefert
    // sie keine validen Klick-Positionen, weshalb das Menue dort vorher
    // unzuverlaessig kam.
    const lc = _localCoords(pane, charX, charY, fractionalCharY, pixelX)
    let posParaId = null
    let posOffset = -1
    if (field === 'text') {
        const pos = view.screenToData(lc.x, lc.y, lc.pixelX)
        if (!pos) return false
        if ((pos.field || 'text') !== 'text') return false
        posParaId = pos.paragraphId
        posOffset = pos.offset
    } else if (field === 'note') {
        const resolved = ctx.editor._resolveNotePosition(lc.x, lc.y, lc.pixelX)
        if (!resolved) return false
        posParaId = resolved.dataPos.paragraphId
        posOffset = resolved.dataPos.offset
    } else if (field === 'info') {
        const resolved = ctx.editor._resolveInfoPosition(lc.x, lc.y, lc.pixelX)
        if (!resolved) return false
        posParaId = resolved.dataPos.paragraphId
        posOffset = resolved.dataPos.offset
    }
    if (!posParaId || posOffset < 0) return false
    if (model.isSeparator(posParaId)) return false

    const warning = ctx.spellCheckManager.warningAt(posParaId, posOffset, field)
    if (!warning) return false

    // Cursor an die Klick-Position bringen — der Aufrufer hat den Cursor
    // im no-selection-Fall zwar schon einmal gesetzt, aber bei aktivem
    // Pane-Wechsel/Switch-Field-Pfad kann er stehenbleiben. Defensiv hier
    // selbst nachziehen, damit Replace und Folge-Eingaben im richtigen
    // Paragraph + Feld landen.
    model.setCursor(posParaId, posOffset, field)

    ctx.spellCheckMenu.show(
        { warning, paragraphId: posParaId, field },
        charX, charY, ctx.screen.cols, ctx.screen.rows
    )
    return true
}

// ============================================================================
// Touch-Handler (iPad/Mobile)
// ----------------------------------------------------------------------------
//   - Single-Finger-Swipe = pixelgenaues 1:1-Scroll + iOS-Style Inertia nach Release
//   - Single-Tap = Klick an der Finger-Position (setzt Cursor, triggert Controls)
//   - Zwei+ Finger bricht den aktiven Touch ab (Pinch-Zoom ist per `touch-action:
//     none` und viewport-Meta ohnehin deaktiviert).
// ============================================================================

const TAP_MAX_DISTANCE_PX = 6    // Finger darf sich so weit bewegen und gilt trotzdem als Tap
const TAP_MAX_DURATION_MS = 400

let _touchState = null
// { paneId, scope, startX, startY, lastX, lastY, startTime, moved, samples }
// scope: 'editor' | 'core' — bestimmt, an welchen Scroll-Pfad delegiert wird
// samples: [{t, y}] für Velocity-Berechnung am touchend

let _pinchState = null

// Nach einem Long-Press feuert iOS-Safari trotz preventDefault auf touchend
// einen synthetischen click an der Tap-Position. Der wuerde das gerade
// geoeffnete Context-Menu sofort wieder schliessen (handleClick → Dashboard/
// Menu sieht "Klick ausserhalb des Menus"). Suppress-Flag mit Safety-Timeout,
// das den NAECHSTEN click in handleClick verschluckt.
let _suppressNextClickUntil = 0
const SUPPRESS_CLICK_WINDOW_MS = 500
// { startDistance: number } — Two-Finger-Pinch fuer Font-Zoom (analog Trackpad)

// Pure Geometrie in touchGeometry.js (V6) — hier nur der Kontext-Anschluss
const _twoFingerDistance = twoFingerDistance

// Inertia-Controller pro Scope — lazy initialisiert, wiederverwendet über Swipes
let _editorInertia = null
let _coreInertia = null

function _getEditorInertia() {
    if (!_editorInertia && ctx.editor && ctx.editor.view) {
        _editorInertia = new InertiaController(
            ctx.editor.view,
            () => { ctx.layout.isDirty = true }
        )
    }
    return _editorInertia
}

function _getCoreInertia() {
    if (!_coreInertia && ctx.coreConsole && ctx.coreConsole.view) {
        _coreInertia = new InertiaController(
            ctx.coreConsole.view,
            () => { ctx.layout.isDirty = true }
        )
    }
    return _coreInertia
}

/**
 * Bricht laufende Touch-Inertia ab. Wird von Wheel/Tastatur-Pfaden aufgerufen,
 * wenn der Nutzer aktiv scrollt — Inertia soll dann sofort stoppen.
 */
export function cancelTouchInertia() {
    if (_editorInertia) _editorInertia.cancel()
    if (_coreInertia) _coreInertia.cancel()
}

/**
 * Phase 5: Findet die aktive Selection und prueft, ob die Touch-Position im
 * Slop um einen ihrer Endpunkte (Anchor oder Cursor) liegt. Liefert das
 * Handle-Hit-Objekt fuer den Drag-Pfad oder null.
 *
 * Pane-Mapping per Selection-Field: 'text'→MAIN, 'note'→OUTLINE, 'info'→CONTEXT.
 * Memory- und Personal-Dict-Editor haben Vorrang (modal, eigene ProseEditor-
 * Instanzen mit field='text' im eigenen Pane).
 *
 * X-Vergleich: bei Proportional in Pixeln gegen `pos.pixelX` (echte
 * Pixel-Position via posMap), sonst in Char-Spalten gegen `pos.x`. Grund:
 * `dataToScreen` liefert für Proportional eine Char-Spalten-Approximation
 * (`markerWidth + hangingOffset + displayX`), die mit Unicode-Display-Width
 * statt echter Pixel-Breite rechnet — bei breiten/schmalen Buchstaben drifted
 * das mit der Zeilenlaenge nach links weg. Tiebreaker: euklidische Distanz.
 *
 * @returns {{editor:Object, paneId:string, end:'anchor'|'cursor', field:string}|null}
 */
function _findSelectionHandleAt(charX, charY, pixelX) {
    const candidates = []
    if (ctx.memoryEditor && ctx.memoryEditor.isActive() && ctx.memoryEditor.proseEditor) {
        candidates.push({ editor: ctx.memoryEditor.proseEditor, paneId: PaneId.MEMORY })
    }
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive() && ctx.personalDictEditor.proseEditor) {
        candidates.push({ editor: ctx.personalDictEditor.proseEditor, paneId: PaneId.PERSONAL_DICT })
    }
    if (ctx.editor) {
        const sel = ctx.editor.model.selection
        if (sel && !sel.isCollapsed()) {
            const field = sel.anchor.field || 'text'
            const paneId = field === 'text' ? PaneId.MAIN
                         : field === 'note' ? PaneId.OUTLINE
                         : field === 'info' ? PaneId.CONTEXT
                         : null
            if (paneId !== null) candidates.push({ editor: ctx.editor, paneId })
        }
    }

    const slop = TOUCH.slop.selectionHandle
    const r = ctx.renderer
    const isProp = r && r.isProportional()
    const cw = r ? r.charWidth : 1
    for (const c of candidates) {
        const positions = c.editor.getSelectionScreenPositions()
        if (!positions) continue
        const pane = ctx.layout.panes[c.paneId]
        if (!pane || !pane.isVisible()) continue
        const ib = pane.innerBounds
        // Tap muss im Pane-Inneren liegen (mit Slop-Puffer am Rand)
        if (!isWithinBoundsWithSlop(ib, charX, charY, slop)) continue
        const lx = charX - ib.x
        const ly = charY - ib.y

        // Pane-lokale Touch-Pixel-X (fuer den Proportional-Pfad).
        const localPixelX = (typeof pixelX === 'number') ? pixelX - ib.x * cw : null

        const check = (pos, end) => {
            const hit = hitTestSelectionEndpoint({
                lx, ly, localPixelX, pos, slop,
                isProportional: isProp, charWidth: cw
            })
            if (!hit) return null
            return { editor: c.editor, paneId: c.paneId, end, field: positions.field, distSq: hit.distSq }
        }
        const a = check(positions.anchor, 'anchor')
        const k = check(positions.cursor, 'cursor')
        if (a && k) return a.distSq <= k.distSq ? a : k
        if (a) return a
        if (k) return k
    }
    return null
}

/**
 * Phase 5: Aktualisiert die laufende Selection mit der aktuellen Touch-Position.
 * Der nicht-gegriffene Endpunkt bleibt auf seinem Original-Wert (Snapshot aus
 * touchstart). Wenn der User das Original-Anchor-Ende greift, wird der bisherige
 * Cursor zum neuen Anchor und der Touch-Punkt zum neuen Cursor.
 *
 * setSelection darf den Anchor/Cursor automatisch umklappen, wenn der User
 * ueber den anderen Endpunkt hinauszieht — die bestehende API kann das.
 */
function _applyDragToSelection(clientX, clientY) {
    const state = _touchState
    if (!state || state.scope !== 'selection-drag') return
    const { editor, paneId, end, field } = state.handleHit
    const pane = ctx.layout.panes[paneId]
    if (!pane || !pane.isVisible()) return
    const ib = pane.innerBounds

    const fake = { clientX, clientY }
    const { charX, charY, pixelX } = ctx.renderer.getCharPosition(fake)
    const localX = charX - ib.x
    const localY = charY - ib.y
    const localPixelX = (typeof pixelX === 'number')
        ? pixelX - ib.x * ctx.renderer.charWidth
        : null

    let result = null
    if (field === 'text') result = editor.view.screenToData(localX, localY, localPixelX)
    else if (field === 'note') result = editor.view.noteScreenToData(localX, localY, localPixelX)
    else if (field === 'info') result = editor.view.infoScreenToData(localX, localY, localPixelX)
    if (!result) return

    const newCursor = new Position(result.paragraphId, result.offset, field)
    const newAnchor = end === 'anchor' ? state.origCursor.clone() : state.origAnchor.clone()
    editor.model.setSelection(newAnchor, newCursor)
    editor.view.markDirty()
    ctx.layout.isDirty = true
}

/**
 * Phase 5: Startet (oder erneuert) den Auto-Scroll-Tick, solange der Finger in
 * der Rand-Zone bleibt. Pro Tick scrollt das aktive Pane um 1 Zeile in die
 * angegebene Richtung und propagiert den Touch-Punkt erneut an die Selection,
 * damit sie sich Frame-fuer-Frame mit dem neuen Viewport mit-erweitert.
 *
 * @param {1|-1} direction - 1 = scroll forward (Finger am unteren Rand),
 *                            -1 = scroll backward (Finger am oberen Rand)
 */
function _startAutoScroll(direction) {
    const state = _touchState
    if (!state || state.scope !== 'selection-drag') return
    if (state.autoScrollDirection === direction && state.autoScrollTimer !== null) return
    _stopAutoScroll()
    state.autoScrollDirection = direction
    const tick = () => {
        const s = _touchState
        if (!s || s.scope !== 'selection-drag' || s.autoScrollDirection === 0) return
        const view = s.handleHit.editor.view
        view.scrollByPixels(direction * ctx.renderer.charHeight)
        _applyDragToSelection(s.lastX, s.lastY)
        s.autoScrollTimer = setTimeout(tick, TOUCH.autoScroll.intervalMs)
    }
    state.autoScrollTimer = setTimeout(tick, TOUCH.autoScroll.intervalMs)
}

function _stopAutoScroll() {
    const state = _touchState
    if (!state) return
    if (state.autoScrollTimer !== null) {
        clearTimeout(state.autoScrollTimer)
        state.autoScrollTimer = null
    }
    state.autoScrollDirection = 0
}

/**
 * Hit-Test: In welchem scrollbaren Pane liegt die Char-Position?
 * Spiegelt die Logik von handleMouseWheel.
 */
function _findScrollPaneAt(charX, charY) {
    // Phase 6.0d Fix: Memory- und Personal-Dict-Editor sind transient-modale
    // FULLSCREEN-Panes — wenn sie aktiv sind, haben MAIN/OUTLINE/CONTEXT/CORE
    // alle bounds.width=0 (siehe `_calculateFullscreenBounds`). Ohne diesen
    // Branch findet handleTouchStart keinen Pane, setzt `_touchState=null`,
    // und handleTouchEnd springt fruehzeitig raus — der Sink-Focus-Trigger
    // (Phase 6.0a) wird also nie erreicht, die System-Tastatur poppt nicht auf.
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()) {
        const memPane = ctx.layout.panes[PaneId.MEMORY]
        if (memPane && memPane.isVisible() && isWithinBounds(memPane.bounds, charX, charY)) {
            return { paneId: PaneId.MEMORY, scope: 'memory' }
        }
    }
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()) {
        const pdPane = ctx.layout.panes[PaneId.PERSONAL_DICT]
        if (pdPane && pdPane.isVisible() && isWithinBounds(pdPane.bounds, charX, charY)) {
            return { paneId: PaneId.PERSONAL_DICT, scope: 'personal-dict' }
        }
    }
    for (const paneId of [PaneId.OUTLINE, PaneId.MAIN, PaneId.CONTEXT]) {
        const pane = ctx.layout.panes[paneId]
        if (!pane || !pane.isVisible()) continue
        if (isWithinBounds(pane.bounds, charX, charY)) {
            return { paneId, scope: 'editor' }
        }
    }
    if (ctx.coreConsole) {
        const corePane = ctx.layout.panes[PaneId.CORE]
        if (corePane && corePane.isVisible() && isWithinBounds(corePane.bounds, charX, charY)) {
            return { paneId: PaneId.CORE, scope: 'core' }
        }
    }
    return null
}

/**
 * Behandelt Touch-Start: merkt den aktiven Scroll-Scope für die Swipe-Dauer.
 */
export function handleTouchStart(e) {
    if (!ctx.editor) return

    // Overlay aktiv (Menue, Dialog, ContextMenu, ProjectDashboard, ...):
    // Schmaler Pfad — nur Long-Press-Timer fuer Phase-3 Context-Menu. Kein
    // Scroll-Handling, kein synthetischer Tap. Den Tap macht iOS-Safari per
    // synthetischem Click-Event, bis Phase 4 echtes Touch-Routing fuer
    // Overlays bringt.
    if (ctx.overlay.isActive) {
        _handleOverlayTouchStart(e)
        return
    }

    // Jede neue Berührung stoppt laufende Inertia — Nutzer greift aktiv ein
    cancelTouchInertia()
    // Jede neue Berührung killt einen evtl. noch laufenden Long-Press-Timer
    _cancelLongPress()

    // Zwei-Finger-Pinch: Font-Zoom (analog Trackpad-Pinch via wheel+ctrlKey).
    // Single-Touch verwerfen, Pinch-State initialisieren.
    if (e.touches.length === 2) {
        _touchState = null
        _pinchState = { startDistance: _twoFingerDistance(e.touches[0], e.touches[1]) }
        e.preventDefault()
        return
    }

    // Drei+ Finger: alles verwerfen, default blocken.
    if (e.touches.length !== 1) {
        _touchState = null
        _pinchState = null
        e.preventDefault()
        return
    }

    const t = e.touches[0]
    const { charX, charY, pixelX } = ctx.renderer.getCharPosition(t)

    // Phase 5: Selection-Handle-Drag hat Vorrang vor Scroll-Pane-Hit-Test.
    // Wenn eine aktive Selection besteht und der Touch im Slop um Anchor oder
    // Cursor liegt, starten wir den Handle-Drag-Modus. Long-Press faellt aus
    // (anders als bei Scroll) — sonst wuerde ein langer Touch auf dem Handle
    // ein Context-Menu oeffnen statt zu greifen. pixelX wird gebraucht, weil
    // bei Proportional die Char-Spalten-Approximation der Selection-Endpunkte
    // mit der Zeilenlaenge nach links drifted.
    const handleHit = _findSelectionHandleAt(charX, charY, pixelX)
    if (handleHit) {
        const sel = handleHit.editor.model.selection
        const now0 = performance.now()
        _touchState = {
            scope: 'selection-drag',
            handleHit,
            paneId: handleHit.paneId,
            // Snapshot der Original-Selection — wird beim Drag gebraucht, damit
            // der nicht-gegriffene Endpunkt stehen bleibt.
            origAnchor: sel.anchor.clone(),
            origCursor: sel.cursor.clone(),
            startX: t.clientX,
            startY: t.clientY,
            lastX: t.clientX,
            lastY: t.clientY,
            startTime: now0,
            moved: false,
            samples: [],
            longPressTimer: null,
            longPressFired: false,
            autoScrollTimer: null,
            autoScrollDirection: 0
        }
        // preventDefault: kein iOS-synth-Click am Ende — der wuerde sonst den
        // Cursor an die Tap-Position setzen und die Selection zerstoeren.
        e.preventDefault()
        return
    }

    const hit = _findScrollPaneAt(charX, charY)
    if (!hit) return

    const now = performance.now()
    _touchState = {
        paneId: hit.paneId,
        scope: hit.scope,
        startX: t.clientX,
        startY: t.clientY,
        lastX: t.clientX,
        lastY: t.clientY,
        startTime: now,
        moved: false,
        // Velocity-Samples: rollendes Fenster der letzten ~inertiaVelocitySampleWindowMs
        samples: [{ t: now, y: t.clientY }],
        // Phase 3: Long-Press feuert einen synthetischen contextmenu-Event.
        // longPressFired schaltet die Tap-Auswertung am Touch-End ab.
        longPressTimer: null,
        longPressFired: false
    }
    _touchState.longPressTimer = setTimeout(() => _fireLongPress(), TOUCH.longPressDurationMs)
    e.preventDefault()
}

/**
 * Long-Press-Callback: dispatcht einen synthetischen `contextmenu`-Event an
 * der aktuellen Touch-Position und unterdrueckt die nachfolgende Tap-Auswertung.
 * Wird nur gerufen, solange `_touchState` noch existiert (Cancel bei Scroll,
 * Tap-Release, Multi-Finger, touchcancel).
 */
function _fireLongPress() {
    if (!_touchState) return
    _touchState.longPressFired = true
    _touchState.longPressTimer = null
    // moved=true → handleTouchEnd loest keinen Tap aus, kein Inertia
    _touchState.moved = true

    const fake = {
        clientX: _touchState.lastX,
        clientY: _touchState.lastY,
        shiftKey: false,
        metaKey: false,
        ctrlKey: false,
        altKey: false,
        button: 2,
        preventDefault: () => {},
        stopPropagation: () => {}
    }
    handleContextMenu(fake)
}

function _cancelLongPress() {
    if (_touchState && _touchState.longPressTimer !== null) {
        clearTimeout(_touchState.longPressTimer)
        _touchState.longPressTimer = null
    }
}

/**
 * Overlay-Touch-Start: minimaler State nur fuer Long-Press. Kein Scroll, kein
 * synthetischer Tap, KEIN preventDefault — iOS-Safari soll den synthetischen
 * Click weiterhin fuer normale Taps dispatchen.
 */
function _handleOverlayTouchStart(e) {
    if (e.touches.length !== 1) return
    _cancelLongPress()
    const t = e.touches[0]
    _touchState = {
        scope: 'overlay',
        paneId: null,
        startX: t.clientX,
        startY: t.clientY,
        lastX: t.clientX,
        lastY: t.clientY,
        startTime: performance.now(),
        moved: false,
        samples: [],   // keine Inertia im Overlay
        longPressTimer: null,
        longPressFired: false
    }
    _touchState.longPressTimer = setTimeout(() => _fireLongPress(), TOUCH.longPressDurationMs)
    // Kein preventDefault — iOS-Click ist Tap-Pfad fuer Overlay-Items
}

/**
 * Behandelt Touch-Move: Delta zum letzten Sample als Pixel-Wheel-Delta
 * an den bei touchstart gemerkten Pane schicken.
 */
export function handleTouchMove(e) {
    // Pinch-Verarbeitung VOR Single-Touch-Check, weil _touchState bei Pinch null ist.
    // Overlay-Guard: falls ein Overlay erst während des laufenden Pinches aufploppt,
    // soll der Font-Zoom nicht durchschlagen (Spec §6 Phase 4: Pinch im Overlay aus).
    if (_pinchState && e.touches.length === 2 && !ctx.overlay.isActive) {
        const dist = _twoFingerDistance(e.touches[0], e.touches[1])
        const ratio = dist / _pinchState.startDistance
        if (ratio >= TOUCH.pinchStepThreshold) {
            handleZoom('in')
            _pinchState.startDistance = dist
        } else if (ratio <= 1 / TOUCH.pinchStepThreshold) {
            handleZoom('out')
            _pinchState.startDistance = dist
        }
        e.preventDefault()
        return
    }

    if (!_touchState) return

    // Phase 5: Selection-Handle-Drag — Finger bewegt einen Selection-Endpunkt.
    // Kein Scroll-Forward, kein Inertia; stattdessen Setselection und Auto-Scroll
    // wenn der Finger am Pane-Rand klebt.
    if (_touchState.scope === 'selection-drag') {
        if (e.touches.length !== 1) {
            // Zweiter Finger → Drag abbrechen (kein Half-State weiterreichen)
            _stopAutoScroll()
            _touchState = null
            if (e.preventDefault) e.preventDefault()
            return
        }
        const t = e.touches[0]
        const totalDx = Math.abs(t.clientX - _touchState.startX)
        const totalDy = Math.abs(t.clientY - _touchState.startY)
        if (totalDx > TAP_MAX_DISTANCE_PX || totalDy > TAP_MAX_DISTANCE_PX) {
            _touchState.moved = true
        }
        _touchState.lastX = t.clientX
        _touchState.lastY = t.clientY

        _applyDragToSelection(t.clientX, t.clientY)

        // Auto-Scroll am Pane-Rand: wenn der Finger im Char-Raster in der
        // edgeZoneRows-Zone (oben oder unten) des Drag-Panes klebt, scrollt
        // ein Timer-Tick das Pane jeweils 1 Zeile in die passende Richtung.
        const pane = ctx.layout.panes[_touchState.handleHit.paneId]
        if (pane && pane.isVisible()) {
            const ib = pane.innerBounds
            const { charY } = ctx.renderer.getCharPosition(t)
            const localY = charY - ib.y
            const edge = TOUCH.autoScroll.edgeZoneRows
            if (localY < edge) _startAutoScroll(-1)
            else if (localY >= ib.height - edge) _startAutoScroll(1)
            else _stopAutoScroll()
        }

        e.preventDefault()
        return
    }

    // Overlay-Pfad: Long-Press-Cancel bei Bewegung. Im ProjectDashboard zusaetzlich
    // Swipe-Scroll an die Liste delegieren (Spec §6 Phase 4 — andere Overlays
    // haben keinen Scroll-Inhalt und ignorieren die Bewegung).
    if (_touchState.scope === 'overlay') {
        if (e.touches.length !== 1) {
            _cancelLongPress()
            _touchState = null
            return
        }
        const t0 = e.touches[0]
        const dx0 = Math.abs(t0.clientX - _touchState.startX)
        const dy0 = Math.abs(t0.clientY - _touchState.startY)
        if (dx0 > TAP_MAX_DISTANCE_PX || dy0 > TAP_MAX_DISTANCE_PX) {
            _cancelLongPress()
            _touchState.moved = true
        }

        // Swipe-Scroll: Frame-Delta an Dashboard reichen, solange offen.
        // dy>0 = Finger nach oben → Inhalt nach oben → forward scroll
        // (gleiche Polaritaet wie editor/core-Scope weiter unten).
        if (_touchState.moved && ctx.projectDashboard && ctx.projectDashboard.isOpen) {
            const dy = _touchState.lastY - t0.clientY
            if (dy !== 0) {
                ctx.projectDashboard.scrollByPixels(dy)
                ctx.layout.isDirty = true
                if (e.preventDefault) e.preventDefault()
            }
        }

        _touchState.lastX = t0.clientX
        _touchState.lastY = t0.clientY
        return
    }

    if (e.touches.length !== 1) {
        // Zweiter Finger während Swipe → abbrechen
        _cancelLongPress()
        _touchState = null
        e.preventDefault()
        return
    }

    // Event-Latency und Handler-Zeit messen (analog zum Wheel-Pfad)
    const handlerStart = performance.now()
    const eventLag = (typeof e.timeStamp === 'number') ? handlerStart - e.timeStamp : 0

    const t = e.touches[0]
    const dy = _touchState.lastY - t.clientY   // Finger runter → Inhalt nach oben → positives deltaY
    const totalDx = Math.abs(t.clientX - _touchState.startX)
    const totalDy = Math.abs(t.clientY - _touchState.startY)

    if (totalDx > TAP_MAX_DISTANCE_PX || totalDy > TAP_MAX_DISTANCE_PX) {
        if (!_touchState.moved) {
            // Erste signifikante Bewegung → Long-Press canceln
            _cancelLongPress()
        }
        _touchState.moved = true
    }

    _touchState.lastX = t.clientX
    _touchState.lastY = t.clientY

    // Velocity-Sample einreihen und altes Fenster trimmen
    const now = performance.now()
    _touchState.samples.push({ t: now, y: t.clientY })
    const windowStart = now - TIMING.inertiaVelocitySampleWindowMs * 2   // etwas großzügiger als die Window-Breite
    while (_touchState.samples.length > 2 && _touchState.samples[0].t < windowStart) {
        _touchState.samples.shift()
    }

    if (_touchState.moved && dy !== 0) {
        // Pixel-genaues 1:1-Scroll direkt auf die jeweilige View (kein Wheel-Umweg).
        if (_touchState.scope === 'editor') {
            ctx.editor.view.scrollByPixels(dy)
        } else if (_touchState.scope === 'core' && ctx.coreConsole) {
            ctx.coreConsole.view.scrollByPixels(dy)
        } else if (_touchState.scope === 'memory'
            && ctx.memoryEditor && ctx.memoryEditor.proseEditor) {
            ctx.memoryEditor.proseEditor.view.scrollByPixels(dy)
        } else if (_touchState.scope === 'personal-dict'
            && ctx.personalDictEditor && ctx.personalDictEditor.proseEditor) {
            ctx.personalDictEditor.proseEditor.view.scrollByPixels(dy)
        }
        ctx.layout.isDirty = true
        scrollProfiler.noteTouch(eventLag, performance.now() - handlerStart)
    }

    e.preventDefault()
}

/**
 * Behandelt Touch-End: ohne nennenswerte Bewegung → Tap = synthetischer Klick.
 */
export function handleTouchEnd(e) {
    // Pinch endet sobald < 2 Finger uebrig — kein nahtloser Uebergang in Scroll
    if (_pinchState && (!e.touches || e.touches.length < 2)) {
        _pinchState = null
        if (e.preventDefault) e.preventDefault()
        if (!_touchState) return
    }

    if (!_touchState) return

    // Overlay-Pfad: Timer canceln und State raeumen. KEIN expliziter Tap-Synthesizer
    // — der iOS-synth-Click hat eine bessere native Hit-Test-Toleranz als unsere
    // rohen touchstart-Koordinaten, und handleClick deckt mehrere Overlay-Branches
    // (ContextMenu, SpellCheckMenu, SearchManager, LinkPicker) gar nicht ab.
    // Long-Press: synth-Click verschlucken, sonst schliesst er das gerade
    // geoeffnete Context-Menu sofort wieder.
    if (_touchState.scope === 'overlay') {
        const fired = _touchState.longPressFired
        _cancelLongPress()
        _touchState = null
        if (fired) {
            _suppressNextClickUntil = performance.now() + SUPPRESS_CLICK_WINDOW_MS
            if (e.preventDefault) e.preventDefault()
        }
        return
    }

    // Phase 5: Selection-Handle-Drag-Ende. Bei moved=true → Selection bleibt
    // stehen, kein Tap-Klick, keine Inertia. Bei moved=false (Tap statt Drag im
    // Handle-Slop) → Selection bleibt ebenfalls unveraendert ("Selection
    // bestaetigt", Spec §7 Edge Case) und kein Cursor-Reset. preventDefault
    // verhindert den iOS-synth-Click, der sonst den Cursor an die Tap-Position
    // setzen wuerde (und die Selection zerstoeren).
    if (_touchState.scope === 'selection-drag') {
        _stopAutoScroll()
        _touchState = null
        _suppressNextClickUntil = performance.now() + SUPPRESS_CLICK_WINDOW_MS
        if (e.preventDefault) e.preventDefault()
        return
    }

    // Long-Press-Timer canceln (falls noch nicht gefeuert)
    _cancelLongPress()

    const duration = performance.now() - _touchState.startTime
    const wasTap = !_touchState.moved && duration < TAP_MAX_DURATION_MS
    const startX = _touchState.startX
    const startY = _touchState.startY
    const scope = _touchState.scope
    const samples = _touchState.samples
    const wasMoved = _touchState.moved
    const wasLongPress = _touchState.longPressFired
    _touchState = null

    // Inertia: nur bei echtem Swipe, nicht bei Long-Press (der hat moved=true,
    // aber keine Scroll-Velocity). Memory- und Personal-Dict-Scope bleiben
    // ohne Inertia — sonst muesste pro offenem Editor-Open ein eigener
    // InertiaController angelegt und beim Schliessen verworfen werden.
    if (wasMoved && !wasLongPress) {
        let inertia = null
        if (scope === 'core') inertia = _getCoreInertia()
        else if (scope === 'editor') inertia = _getEditorInertia()
        if (inertia) {
            const velocity = computeVelocity(samples)
            inertia.start(velocity)
        }
    }

    if (wasTap) {
        // Synthetischer MouseEvent an der Tap-Position — handleClick erwartet
        // clientX/clientY und die üblichen Mouse-Flags. Shift/Meta bleiben false.
        const fake = {
            clientX: startX,
            clientY: startY,
            shiftKey: false,
            metaKey: false,
            ctrlKey: false,
            altKey: false,
            button: 0,
            preventDefault: () => {},
            stopPropagation: () => {}
        }

        // Touch-Slop fuer Separator-Controls im Outline: Sep-Hit-Test laeuft ueber
        // handleNoteClick mit pixelgenauer Position — fuer touch-tolerante Treffer
        // probieren wir die Slop-Box hier vorab und feuern direkt das Control.
        const sepCtrl = _findSeparatorControlForTouchTap(fake)
        if (sepCtrl) {
            ctx.editor._handleSeparatorControl(sepCtrl)
            ctx.layout.isDirty = true
        } else {
            // Phase 6.0a: Tap in einen Text-Bereich → versteckten Sink synchron
            // fokussieren, damit iOS / iPadOS die System-Tastatur einblendet.
            // MUSS hier vor handleClick stehen, weil iOS focus() aus async-
            // Pfaden (Promise/setTimeout) verweigert und der Tap-Handler die
            // letzte User-Gesture-Stelle im Stack ist. Nicht-Text-Bereiche
            // (Border, Control, Tab-Leiste, Pane-Title, leerer Bereich) gehen
            // bewusst leer aus — kein Sink-Focus, keine Tastatur.
            //
            // Phase 6.0d: Tap auf nicht-Text-Bereich blurrt den Sink (falls
            // fokussiert) → System-Tastatur schliesst sich. Scroll-Swipes laufen
            // nicht ueber diesen Pfad (das ist der Tap-Pfad), bleiben also ohne
            // Blur — Tastatur ueberlebt versehentliche Wischer.
            if (_isTextAreaTap(fake)) {
                focusKeyboardSink()
            } else {
                blurKeyboardSink()
            }

            // Tap-charY ZUERST merken, bevor handleClick evtl. showTabBar triggert
            // (Layout-Shift haette danach charY um 1 verschoben).
            const tapCharY = ctx.renderer.getCharPosition(fake).charY
            const tabBarVisibleBefore = ctx.layout.tabBarVisible

            handleClick(fake, { touchSlop: true })

            // Touch-Dismiss: Tab-Leiste schliessen, wenn sie vor dem Tap schon
            // sichtbar war und der Tap NICHT im Trigger-Bereich lag. Falls der
            // Tap die Tab-Leiste gerade erst geoeffnet hat (tapCharY innerhalb
            // Trigger-Zone) → nicht zumachen.
            if (tabBarVisibleBefore && ctx.layout.tabBarVisible
                && tapCharY >= TOUCH.tabBarTrigger.height) {
                ctx.layout.hideTabBar()
                if (ctx.tabBarRenderer) ctx.tabBarRenderer.clearHover()
                _resizeAfterTabBarChange()
            }
        }
    }

    e.preventDefault()
}

/**
 * Phase 6.0a: True, wenn der Tap an einer Stelle landet, an der wir tippbaren
 * Text erwarten — Main.text, Outline-Note, Context-Info, Core-Input, oder die
 * inneren Bereiche der transient-modalen Memory- und Personal-Dict-Editoren.
 *
 * Alles andere (offene Overlays, Tab-Leiste, Border-Controls, Pane-Titel,
 * Bereiche ausserhalb der Pane-innerBounds) gibt false zurueck — die System-
 * Tastatur soll dort gar nicht erst aufpoppen. Spec §8 Aktivierungs-Regel:
 * "wenn unklar — bewusst kein Focus, User muss nochmal explizit setzen".
 *
 * @returns {boolean}
 */
function _isTextAreaTap(fakeEvent) {
    // Geoeffnete Overlays kassieren den Tap → kein Text-Bereich.
    if (ctx.userMenu && ctx.userMenu.isOpen) return false
    if (ctx.dialogManager && ctx.dialogManager.isDialogOpen()) return false
    if (ctx.projectDashboard && ctx.projectDashboard.isOpen) return false
    if (ctx.menu && ctx.menu.isMenuOpen()) return false
    if (ctx.contextMenu && ctx.contextMenu.isOpen) return false
    if (ctx.spellCheckMenu && ctx.spellCheckMenu.isOpen) return false
    if (ctx.searchManager && ctx.searchManager.isOpen) return false
    if (ctx.editor && ctx.editor.input
        && typeof ctx.editor.input.getLinkPickerState === 'function'
        && ctx.editor.input.getLinkPickerState()) return false

    const { charX, charY } = ctx.renderer.getCharPosition(fakeEvent)

    // Tab-Leiste (oben, y=0 wenn sichtbar) ist Steuerleiste, kein Text.
    if (ctx.layout.tabBarVisible && charY === 0) return false

    // Border-Control mit dem gleichen Slop wie der Tap-Hit-Test → Steuerung,
    // nicht Text. Pinch/Lock/Collapse-Controls fallen alle hier raus.
    const ctrlSlop = ctx.renderer.isProportional()
        ? TOUCH.slop.borderControlProp
        : TOUCH.slop.borderControl
    if (ctx.layout.getControlAtPosition(charX, charY, ctrlSlop)) return false

    // Transient-modale Editoren (Memory / Personal-Dict): nur ihr Inneres
    // gilt als Text-Bereich, alles andere ist Hintergrund.
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()) {
        const memPane = ctx.layout.panes[PaneId.MEMORY]
        return !!(memPane && memPane.isVisible()
            && _isWithinPaneInnerBounds(memPane, charX, charY))
    }
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()) {
        const pdPane = ctx.layout.panes[PaneId.PERSONAL_DICT]
        return !!(pdPane && pdPane.isVisible()
            && _isWithinPaneInnerBounds(pdPane, charX, charY))
    }

    // Welcher Pane traegt den Tap? Nur die vier tippbaren Bereiche.
    const pane = ctx.layout.getPaneAtPosition(charX, charY)
    if (!pane || !pane.isExpanded()) return false
    if (pane.id !== PaneId.MAIN && pane.id !== PaneId.OUTLINE
        && pane.id !== PaneId.CONTEXT && pane.id !== PaneId.CORE) {
        return false
    }
    // Pane-Titelzeile (charY === pane.bounds.y) ist die Topborder mit Titel —
    // kein Text. innerBounds beginnt eine Zeile darunter.
    return _isWithinPaneInnerBounds(pane, charX, charY)
}

function _isWithinPaneInnerBounds(pane, charX, charY) {
    return isWithinBounds(pane.innerBounds, charX, charY)
}

/**
 * Probiert die Slop-Box um die Tap-Position durch, bis getSeparatorControlAtPosition
 * einen Treffer liefert (oder gibt null zurueck). Nur im Outline-Pane sinnvoll.
 * @returns {{type:string, paragraphId:number}|null}
 */
function _findSeparatorControlForTouchTap(fakeEvent) {
    if (!ctx.editor || !ctx.editor.outlineView) return null
    const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
    if (!outlinePane || !outlinePane.isVisible()) return null

    const { charX, charY } = ctx.renderer.getCharPosition(fakeEvent)
    const ib = outlinePane.innerBounds
    // Nur wenn Tap innerhalb des Outline-Pane-Inneren liegt (mit Slop-Toleranz)
    const slop = TOUCH.slop.separatorControl
    if (!isWithinBoundsWithSlop(ib, charX, charY, slop)) return null

    // Slop-Spirale: exakte Position zuerst, dann naehester Treffer in der Box
    return findNearestHitWithSlop(
        charX - ib.x, charY - ib.y, slop,
        (x, y) => ctx.editor.outlineView.getSeparatorControlAtPosition(x, y)
    )
}

/**
 * Touch-Cancel: wie End, aber ohne Tap-Auswertung.
 */
export function handleTouchCancel(e) {
    _cancelLongPress()
    _stopAutoScroll()
    _touchState = null
    _pinchState = null
    // Inertia nicht starten — abgebrochen bedeutet keine definierte End-Velocity
    cancelTouchInertia()
    if (e && e.preventDefault) e.preventDefault()
}
